/**
 * merge-images / app.js — UI wiring.
 *
 * Holds no geometry and no pixel logic: it turns controls into a settings
 * object, asks layout.js for a plan, and hands the plan to compose.js (preview)
 * or export.js (file). Everything worth testing lives in those modules and is
 * covered by Node tests; this file is the part that has to be looked at.
 *
 * Rebuilt 2026-09-08 onto the console layout. What changed here, and why:
 *
 *  • The page was a 900 px column of eight cards. Measured with files loaded,
 *    the preview sat 1.2–2.9 screens down and the Download button 2.5–5.7
 *    screens down on every size we ship to. Rail + stage fixes the structure;
 *    this file follows the new markup.
 *  • The tool's own document-level `paste` handler duplicated the shared
 *    uploader's, so one Ctrl+V added the same screenshot TWICE (measured, 2 for
 *    1 and 4 for 2) — on a tool whose upload hint advertises pasting. The
 *    handler is gone: the uploader is the front door, and the tool listens to
 *    `tool-uploader:file` only.
 *  • Five "quick start" chips sat above four mode tabs asking the same
 *    question. The mode segment is now the single layout control, and the two
 *    presets worth keeping became offers that appear when the loaded files make
 *    them possible.
 *  • Per-image controls lived in three places (rotate/flip on the thumbnail,
 *    opacity/blend in a "Layer" accordion, caption in a "Captions" accordion).
 *    They are one panel about one subject now.
 *
 * New behaviour: tone matching (harmonize.js), the before/after divider,
 * on-canvas transform handles for the overlay mode, and Web Share.
 */

'use strict';

import * as Layout from './layout.js';
import * as Stitch from './stitch.js';
import * as Strip from './strip.js';
import * as Compose from './compose.js';
import * as Exporter from './export.js';
import * as Harmonize from './harmonize.js';
import * as LayerMask from './layer-mask.js';
import { decodeFile, release } from './decode.js';

const SLUG = 'merge-images';
const I18N = window.MI_I18N || {};
const SIG_W = 256;                 // signature width; rows stay exact
const MAX_ITEMS = 40;

const $ = (id) => document.getElementById(id);
const t = (k, vars) => {
    let s = I18N[k] || k;
    if (vars) for (const key in vars) s = s.split(':' + key).join(vars[key]);
    return s;
};
const ae = (ev, payload) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, payload)); } catch (_) {} };

/**
 * Funnel stage. The shared tier-0 layer turns these into `tool_stage` rows and,
 * crucially, stamps the LAST stage onto the automatic `tool_abandon` — without
 * calling it, every abandon ships with a null stage and the funnel can only say
 * "someone left", never "someone left while the export was running".
 */
const stage = (name, meta) => { try { window.__stage && window.__stage(name, meta || {}); } catch (_) {} };
const fmtBytes = (n) => n < 1024 ? n + ' B'
    : n < 1048576 ? (n / 1024).toFixed(0) + ' KB'
        : (n / 1048576).toFixed(2) + ' MB';
const show = (id, on) => { const e = $(id); if (e) e.classList.toggle('mi-hidden', !on); };

/**
 * Make a user-typed string safe to hand to a download.
 *
 * The name field is free text, and a slash in it turns "merged" into a path.
 * Browsers mostly sanitise the `download` attribute themselves, but "mostly"
 * across every engine and every save dialog is not a guarantee worth relying
 * on, and Windows additionally rejects : * ? " < > | outright — a name carrying
 * those can fail the save with no explanation.
 */
/* The name the person typed, as a file name. Through the shared cleaner
   (public/js/output-filename.js): until 01.10.2026 this was a regex of its own whose
   control-character range had been written as raw bytes — a sweep of broken
   characters removed them, and «\x00-\x1f» became « -», so every SPACE turned into
   a dash («Мой коллаж» → «Мой-коллаж.jpg») and control characters went through. */
function safeName(s) {
    const on = window.__outputName;
    let cleaned = on && typeof on.sanitize === 'function'
        ? on.sanitize(String(s || ''))
        : String(s || '').replace(/[\\/:*?"<>|\x00-\x1f]/g, '-').replace(/\s+/g, ' ').replace(/^\.+/, '').trim();
    cleaned = cleaned.slice(0, 60).trim();
    return cleaned || 'merged';
}

/* ── state ────────────────────────────────────────────────────────────── */

const S = {
    items: [],
    mode: 'horizontal',            // "two photos side by side" is the job people arrive with
    columns: 2,
    fit: 'match',
    matchTarget: 'max',
    targetPx: 1080,
    align: 'center',
    gap: 0,
    padding: 0,
    radius: 0,
    bgMode: 'transparent',
    bgColor: '#ffffff',
    borderW: 0,
    borderC: '#ffffff',
    // The joins of a strip. What they may do, and in which layout, is decided in
    // strip.js — nothing below reads these three directly to build a plan.
    autoStitch: false,
    trimTop: 0,
    trimBottom: 0,
    seams: [],                     // overlaps of a COLUMN (measured, or set by hand)
    seamsH: [],                    // overlaps of a ROW (set by hand only)
    captionsOn: false,
    captionSize: 28,
    captionColor: '#ffffff',
    canvasMode: 'first',
    // Tone match across the set — off until asked for.
    harmonize: false,
    harmonizeRef: 'first',
    // Before/after decoration.
    compareOn: false,
    labelA: '',
    labelB: '',
    divider: 6,
    dividerC: '#ffffff',
    format: 'png',
    quality: 92,
    maxOut: 0,
    filename: 'merged',
    selected: 0,
    busy: false,
};

let uid = 0;
let lastPlan = null;
let lastCorrections = null;

/* The work on the page gets REPLACED by a restore and by «start over»: pictures
   still decoding for the replaced work must not land in the new one. */
let workGen = 0;
let adding = 0;          // picks still decoding — the person has already started something
let restoring = null;    // a saved work coming back; a pick made meanwhile joins it
let resetting = null;    // «start over» filing the old work away first; a pick made meanwhile waits for the clean page
let resetSettle = null;
let resetsDue = 0;       // clean-ups the controller still owes (a double click asks twice)
let waitingPicks = 0;    // picks made during either — they are the NEXT work

/* ── item model ───────────────────────────────────────────────────────── */

function newItem(decoded) {
    return {
        id: ++uid,
        d: decoded,
        w: decoded.w, h: decoded.h,
        hasAlpha: decoded.hasAlpha,
        rotate: 0, flipH: false, flipV: false,
        opacity: 1, blend: 'normal',
        caption: '',
        trim: { t: 0, r: 0, b: 0, l: 0 },
        x: 0, y: 0, scale: 1, angle: 0,
        sig: null, sigH: 0,
        tone: null,                 // harmonize.measure() result, cached per source
        // Hidden parts in «Слоями» (layer-mask.js): the brush's mask in the source's
        // own pixels, and a soft edge. `masked` says whether THIS render draws them.
        mask: null, maskRev: 0, fade: { side: 'none', size: 0.25 }, masked: false,
    };
}

/** Metrics the layout engine consumes — never bitmaps. */
function metricsFor(it) {
    return {
        w: it.w, h: it.h,
        rotate: it.rotate, flipH: it.flipH, flipV: it.flipV,
        trim: it.trim,
        opacity: it.opacity, blend: it.blend,
        caption: S.captionsOn ? it.caption : '',
        x: it.x, y: it.y, scale: it.scale, angle: it.angle,
    };
}

/**
 * What the joins do in the layout on the page right now.
 *
 * One door for the plan builder, the status line and the report. Before it the
 * crops and overlaps were read straight off the state in every mode — which is
 * how a column's crops reached photos standing side by side (strip.js).
 */
function joinsNow() {
    return Strip.joinsFor(S.mode, S.items.length, {
        trimTop: S.trimTop, trimBottom: S.trimBottom,
        seams: { vertical: S.seams, horizontal: S.seamsH },
    });
}

/** The overlaps of the strip the page is showing — the list the seam sliders edit. */
function seamList() { return S.mode === 'horizontal' ? S.seamsH : S.seams; }

/** A new set of frames, or a new order: every overlap was measured on other pairs. */
function dropSeams() { S.seams = []; S.seamsH = []; }

/** The ordered frames on the page. A stitch offer is about them and about nothing else. */
function setKey() { return S.items.map((it) => it.id).join(','); }

function layoutOpts(extra) {
    return Object.assign({
        mode: S.mode,
        columns: S.columns,
        fit: S.mode === 'overlay' ? 'none' : S.fit,
        matchTarget: S.matchTarget === 'custom' ? S.targetPx : S.matchTarget,
        align: S.align,
        gap: S.gap,
        padding: S.padding,
        radius: S.radius,
        background: S.bgMode === 'transparent' ? 'transparent' : S.bgColor,
        border: { width: S.borderW, color: S.borderC },
        maxOutput: S.maxOut,
        captionHeight: S.captionsOn ? Math.round(S.captionSize * 1.6) : 0,
        canvas: S.canvasMode,
        seams: joinsNow().seams,
    }, extra || {});
}

function buildPlan(extra) {
    // The crops of the joins are edge crops on the frames — expressed through
    // the same `trim` the per-image crop uses, so the engine has one crop
    // concept rather than two that can disagree. WHICH frames lose what, and in
    // which layout, is strip.js's answer; nothing here reads the stitch state.
    const items = Strip.cropByJoins(S.items.map(metricsFor), joinsNow());
    const plan = Layout.plan(items, layoutOpts(extra));
    applyTone(plan);
    return plan;
}

/* ── tone matching ────────────────────────────────────────────────────── */

/**
 * Attach the per-photo colour correction to the plan's placements.
 *
 * The measurement is cached on the item (it depends on the SOURCE pixels, which
 * only a trim or a re-decode can change) while the correction is recomputed
 * whenever the set or the reference changes — adding a sixth photo genuinely
 * moves the median.
 */
function applyTone(plan) {
    if (!S.harmonize || !Harmonize.supported() || S.items.length < 2) {
        lastCorrections = null;
        return;
    }
    const stats = S.items.map((it) => {
        if (!it.tone) {
            try {
                it.tone = Harmonize.measure(it.d.drawable, Layout.sourceRect({ w: it.w, h: it.h, trim: it.trim }));
            } catch (_) { it.tone = null; }
        }
        return it.tone;
    });
    lastCorrections = Harmonize.corrections(stats, S.harmonizeRef);
    for (const p of plan.placements) {
        const f = Harmonize.filterString(lastCorrections[p.i]);
        if (f) p.filter = f;
    }
}

/** The line under the switch: what it will actually do to THESE photos. */
function harmonizeStatus() {
    const el = $('miHarmonizeHint');
    if (!el) return;
    if (!S.harmonize) { el.textContent = t('harmonize_hint'); return; }
    const changed = lastCorrections ? lastCorrections.filter((c) => c && c.changed).length : 0;
    const stops = S.items.length > 1
        ? Harmonize.spreadStops(S.items.map((i) => i.tone))
        : 0;
    el.textContent = changed
        ? t('harmonize_applied', { n: changed, stops: stops.toFixed(1) })
        : t('harmonize_nothing');
}

/* ── before/after decoration ──────────────────────────────────────────── */

/**
 * Divider + labels, or null.
 *
 * Only for exactly two photos in a strip layout: a line between six frames is a
 * grid, and a grid does not need one. Refusing here rather than in the drawing
 * code keeps "is this meaningful?" in one place.
 */
function decorFor(plan) {
    if (!S.compareOn || S.items.length !== 2) return null;
    if (S.mode !== 'horizontal' && S.mode !== 'vertical') return null;
    const base = Math.min(plan.width, plan.height);
    return {
        axis: S.mode,
        divider: S.divider * (plan.scale || 1),
        dividerColor: S.dividerC,
        labels: [S.labelA || '', S.labelB || ''],
        labelSize: Math.max(11, Math.round(base * 0.055)),
        labelColor: '#ffffff',
        labelBg: 'rgba(0,0,0,.62)',
        family: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    };
}

function compareApplies() {
    return S.items.length === 2 && (S.mode === 'horizontal' || S.mode === 'vertical');
}

/* ── signatures / auto stitch ─────────────────────────────────────────── */

/**
 * Build a row signature in OUTPUT space.
 *
 * Height is the item's height as the current layout places it, so a detected
 * overlap is already in the units the plan speaks — no rescaling step that can
 * drift. Width is squashed to SIG_W because only rows carry the alignment
 * information; that keeps a 12 MP screenshot's signature at ~2 MB.
 */
function signatureFor(it, outH) {
    const src = Layout.sourceRect({ w: it.w, h: it.h, trim: it.trim });
    /* The signature is a function of the pixels read and the height they are
       read at — and of nothing else, so it is kept until one of those changes.
       It used to be thrown away on every refresh, which made the detector's
       answer for a pair impossible to keep: the same two frames were searched
       again in full on every change of layout (a second for three phone
       screenshots, minutes for long ones — stitch.js MAX_DEPTH). */
    const key = [Math.max(1, Math.round(outH)), src.x, src.y, src.w, src.h].join(':');
    if (it.sig && it.sigKey === key) return it.sig;
    const c = document.createElement('canvas');
    c.width = SIG_W; c.height = Math.max(1, Math.round(outH));
    const cx = c.getContext('2d', { willReadFrequently: true });
    cx.imageSmoothingQuality = 'high';
    cx.drawImage(it.d.drawable, src.x, src.y, src.w, src.h, 0, 0, c.width, c.height);
    const data = cx.getImageData(0, 0, c.width, c.height).data;
    it.sig = Stitch.signatureFromRGBA(data, c.width, c.height);
    it.sigKey = key;
    it.sigH = outH;
    return it.sig;
}

/**
 * What the frames say about themselves when stood in a column: the bars every
 * one of them repeats, the rows neighbours share — and whether any of that
 * PROVES they are captures of one page (Stitch.captureVerdict).
 *
 * Measured on the frames, not on the mode the page happens to be in: whether
 * five screenshots are one scrolled page is a fact about them.
 */
function measureColumn() {
    const base = Layout.plan(S.items.map(metricsFor), layoutOpts({ mode: 'vertical', seams: null, maxOutput: 0 }));
    const res = Stitch.autoSeams(S.items.map((it, i) => signatureFor(it, base.placements[i].h)));
    res.verdict = Stitch.captureVerdict(res);
    return res;
}

/**
 * Measure the joins of the column and apply them — because the PERSON said
 * these are captures: the switch in the drawer, or the offer they accepted.
 *
 * That statement is the one thing the page cannot work out by itself. Fixed
 * bars are no evidence of it (two photos with sky in them "repeat rows at the
 * top"), so the page never acts on them unasked — maybeOfferStitch() waits for
 * proof. Once the person has said it, the bars ARE bars and are cut, shared
 * rows or not: frames scrolled a full screen at a time share nothing and still
 * carry a status bar each.
 *
 * What changed on 2026-10-07 is everything around that: the offer no longer
 * goes out on bars alone (#1840 — 425 px off a photo), a found seam is verified
 * before it removes a row (stitch.js), the result acts in a column only
 * (strip.js), and the toast below says what was cut instead of «no repeats
 * found» over a picture that had just lost a quarter of itself.
 */
function runAutoStitch() {
    if (S.items.length < 2 || S.mode !== 'vertical') return null;
    const res = measureColumn();
    S.trimTop = res.chrome.top;
    S.trimBottom = res.chrome.bottom;
    S.seams = res.seams.map((s) => ({ overlap: s.overlap, cross: s.cross, confidence: s.confidence, auto: s.auto }));
    const found = S.seams.filter((s) => s.auto).length;
    const bars = S.trimTop + S.trimBottom;
    ae('feature_use', {
        f: 'auto_stitch', n: S.items.length, found, proven: res.verdict.proven,
        chrome_top: S.trimTop, chrome_bottom: S.trimBottom,
    });
    window.__toast && window.__toast(
        found ? t('stitch_found', { n: found, total: res.seams.length })
            : (bars && I18N.stitch_bars) ? t('stitch_bars', { top: S.trimTop, bottom: S.trimBottom })
                : t('stitch_none'),
        found ? 'success' : 'info'
    );
    syncStitchUI();
    return res;
}

/**
 * Sliders moved by hand: the stitch is the person's now, and nothing measures
 * over it. Left on, the switch re-measured the column at the next change of the
 * set and replaced the hand-set numbers without a word.
 */
function stitchByHand() {
    if (!S.autoStitch) return;
    S.autoStitch = false;
    $('miAutoStitch').checked = false;
    syncStitchState();
}

/**
 * Undo the stitch: every frame whole again, butted, in the column.
 * The way back from the offer's button — and what the switch does when turned off.
 */
function revertStitch(via) {
    S.autoStitch = false;
    $('miAutoStitch').checked = false;
    S.seams = [];
    S.trimTop = 0;
    S.trimBottom = 0;
    if (S._preset === 'screenshot') S._preset = '';
    // the person has answered for these frames: the offer does not come straight back
    S._offerDeclined = setKey();
    ae('feature_use', { f: 'stitch_reverted', via: via || 'switch' });
    syncStitchUI();
    scheduleRender();
    draftTouch(true);
}

/**
 * The line over the save row that says a stitch is ON, with the way back on it.
 *
 * Accepting the offer cuts rows out of the person's pictures. That has to stay
 * visible where it happened and be one press from undone — the switch that
 * undoes it sits in a drawer of the rail, a screen away on a phone.
 */
function syncStitchState() {
    const bar = $('miStitchState');
    // (no line and no words for it on a page older than this script — see wire())
    if (!bar || !I18N.stitch_state) return;
    const joins = joinsNow();
    const on = S.autoStitch && S.mode === 'vertical' && (joins.trims || joins.seams);
    if (on) {
        $('miStitchStateText').textContent = t('stitch_state', {
            n: S.seams.filter((s) => s && s.overlap > 0).length,
            total: Math.max(0, S.items.length - 1),
            px: Math.round(Strip.removedBy(joins, S.items.length)),
        });
    }
    show('miStitchState', !!on);
}

function syncStitchUI() {
    const column = S.mode === 'vertical';
    // The switch and the two crops are about a COLUMN of captures. A row of
    // frames has only its overlaps, set by hand.
    show('miStitchAuto', column);
    const shortest = S.items.reduce((m, it) => Math.min(m, it.h), Infinity);
    // A slider that cannot show the number it stands for lies about it: the
    // measured bars reach a quarter of a frame, and the track used to end at 400.
    const cropMax = String(Math.max(400, isFinite(shortest) ? Math.round(shortest / 2) : 0, S.trimTop, S.trimBottom));
    $('miTrimTop').max = cropMax; $('miTrimBottom').max = cropMax;
    $('miTrimTop').value = S.trimTop; $('miTrimTopV').textContent = S.trimTop;
    $('miTrimBottom').value = S.trimBottom; $('miTrimBottomV').textContent = S.trimBottom;
    syncStitchState();
    const box = $('miSeams');
    box.textContent = '';
    if (S.items.length < 2 || S.mode === 'overlay' || S.mode === 'grid') return;
    const list = seamList();
    for (let i = 0; i + 1 < S.items.length; i++) {
        const s = list[i] || { overlap: 0, confidence: 0 };
        const row = document.createElement('div');
        row.className = 'mi-seam';
        const label = document.createElement('span');
        label.className = 'mi-seam-n';
        const dot = document.createElement('i');
        dot.className = 'mi-conf ' + (s.confidence >= 0.7 ? 'hi' : s.confidence > 0 ? 'lo' : '');
        dot.title = t('seam_confidence', { p: Math.round((s.confidence || 0) * 100) });
        label.appendChild(dot);
        label.appendChild(document.createTextNode(t('seam_n', { a: i + 1, b: i + 2 })));
        const range = document.createElement('input');
        range.type = 'range'; range.min = '0'; range.step = '1';
        // along the strip: rows of a column, columns of a row
        const along = (it) => (column ? it.h : it.w);
        range.max = String(Math.max(50, Math.round(Math.min(along(S.items[i]), along(S.items[i + 1])) * 0.9), s.overlap || 0));
        range.value = String(s.overlap || 0);
        range.setAttribute('aria-label', t('seam_n', { a: i + 1, b: i + 2 }));
        const val = document.createElement('span');
        val.className = 'mi-seam-v';
        val.textContent = (s.overlap || 0) + ' px';
        range.addEventListener('input', () => {
            // The list has to cover EVERY join or the plan refuses it (a list one
            // short was measured on other frames). It used to be grown by index:
            // in a set of three with nothing measured, the first slider wrote a
            // list of one and moved nothing until the last slider was touched.
            const live = seamList();
            for (let j = 0; j + 1 < S.items.length; j++) {
                if (!live[j]) live[j] = { overlap: 0, cross: 0, confidence: 0, auto: false };
            }
            live.length = S.items.length - 1;
            const prev = live[i] || {};
            // A hand-corrected seam is the detector being WRONG, and it is the
            // only signal that says so — a seam nobody touches might be right or
            // might just be unnoticed. Reported once per seam, with how far off
            // the automatic answer was, so the tuning has real numbers behind it.
            if (prev.auto && !prev.corrected) {
                ae('feature_use', {
                    f: 'seam_corrected', seam: i,
                    was: prev.overlap, now: +range.value,
                    delta: +range.value - prev.overlap,
                    confidence: Math.round((prev.confidence || 0) * 100),
                });
            }
            live[i] = Object.assign({ cross: 0, confidence: 0 }, prev, {
                overlap: +range.value, auto: false, corrected: true,
            });
            val.textContent = range.value + ' px';
            dot.className = 'mi-conf';
            stitchByHand();
            syncStitchState();
            scheduleRender();
        });
        row.append(label, range, val);
        box.appendChild(row);
    }
}

/* ── preview ──────────────────────────────────────────────────────────── */

const canvas = $('miCanvas');
const ctx = canvas ? canvas.getContext('2d') : null;
let rafId = 0;
let view = { scale: 1, backing: 1 };   // plan px → CSS px, and CSS px → device px

function scheduleRender() {
    if (rafId) return;
    rafId = requestAnimationFrame(() => { rafId = 0; renderPreview(); });
}

function renderPreview() {
    if (!S.items.length || !ctx) return;
    const plan = buildPlan();
    lastPlan = plan;

    const box = $('miCanvasBox');
    const availW = Math.max(140, box.clientWidth - 20);
    // The box is content-sized now, so its own height cannot be the budget —
    // that would feed the previous frame's answer back in and the preview would
    // walk itself down to the minimum over a few renders. The CEILING is the
    // budget, and it lives in one place: the max-height CSS declares.
    const capH = parseFloat(getComputedStyle(box).maxHeight);
    const availH = Math.max(110, (isFinite(capH) ? capH : 320) - 20);
    // A long stitch fitted to HEIGHT shrinks to a matchstick nobody can read.
    // Past roughly 2.5:1 the useful view is full width with vertical scrolling —
    // the same way you'd look at the screenshots it was made from.
    const tall = plan.height / plan.width > 2.5;
    box.classList.toggle('tall', tall);
    const scale = tall
        ? Math.min(1, availW / plan.width)
        : Math.min(1, availW / plan.width, availH / plan.height);
    const cssW = Math.max(1, Math.round(plan.width * scale));
    const cssH = Math.max(1, Math.round(plan.height * scale));

    canvas.style.width = cssW + 'px';
    canvas.style.height = cssH + 'px';
    // The preview canvas IS composited, so its backing store must stay under the
    // device's GPU texture limit — over it the canvas paints BLANK on mobile while
    // getImageData still returns pixels, which makes the bug invisible to tests.
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    let s = dpr;
    if (window.__fitCanvasBacking) s = window.__fitCanvasBacking(canvas, cssW, cssH, dpr);
    else { canvas.width = Math.round(cssW * dpr); canvas.height = Math.round(cssH * dpr); }
    view = { scale, backing: s };

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    Compose.draw(ctx, plan, drawables(), {
        scale: scale * s,
        captionStyle: captionStyle(),
        decor: decorFor(plan),
    });
    drawHandles(plan, scale * s);

    updateMeta(plan);
    harmonizeStatus();
    // the offer and the stitch switch change between renders: the door follows the page
    syncTogetherDoor();
}

function drawables() {
    const out = [];
    // Hidden parts belong to the layering: in a strip a photo with a hole would
    // be a photo with a hole, so outside «Слоями» every photo is drawn whole.
    const overlay = S.mode === 'overlay';
    S.items.forEach((it, i) => {
        it.masked = overlay && LayerMask.hasMask(it);
        out[i] = it.masked
            ? LayerMask.drawableFor(it, it.d.drawable, Layout.sourceRect({ w: it.w, h: it.h, trim: it.trim }))
            : it.d.drawable;
    });
    return out;
}

function captionStyle() {
    if (!S.captionsOn) return null;
    return {
        size: S.captionSize,
        color: S.captionColor,
        weight: 600,
        family: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    };
}

/* ── overlay: direct manipulation ─────────────────────────────────────── */

const HANDLE = 9;          // CSS px, radius of a corner grip
const ROT_ARM = 26;        // CSS px, distance of the rotate grip above the box

/**
 * Corner + rotate-grip positions of a placement, in PLAN coordinates.
 *
 * ⚠️ Every grip is CLAMPED into the canvas. In "canvas: first" the frame is the
 * first photo's box, so a layer dragged half out of frame has its corners —
 * and its rotate arm — outside the drawn area, where nothing paints and nothing
 * can be clicked: the controls for the selected layer simply stop existing, and
 * the only way back is to drag it home by guesswork. Measured on the rebuild
 * (2026-09-08): after one ordinary drag the scale grip was 96 px past the right
 * edge and a click on it did nothing at all.
 *
 * Clamping keeps the maths honest because the scale reference distance `d0` is
 * measured from the pointer at grab time, not from the corner — a grip pulled
 * inward is still a proportional handle.
 */
function gripsFor(p, plan) {
    const cx = p.x + p.w / 2, cy = p.y + p.h / 2;
    const a = (p.angle || 0) * Math.PI / 180;
    const cos = Math.cos(a), sin = Math.sin(a);
    const rot = (dx, dy) => ({ x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos });
    const hw = p.w / 2, hh = p.h / 2;
    const armPlan = ROT_ARM / Math.max(0.0001, view.scale);
    const inset = (HANDLE + 3) / Math.max(0.0001, view.scale);
    const W = plan ? plan.width : Infinity;
    const H = plan ? plan.height : Infinity;
    const hold = (q) => (plan ? {
        x: Math.max(inset, Math.min(W - inset, q.x)),
        y: Math.max(inset, Math.min(H - inset, q.y)),
    } : q);

    const corners = [rot(-hw, -hh), rot(hw, -hh), rot(hw, hh), rot(-hw, hh)].map(hold);
    // Above the box normally; a layer at the top of the canvas gets it below,
    // because a grip drawn off the picture is a control that cannot be reached.
    let grip = rot(0, -hh - armPlan);
    if (plan && grip.y < inset + armPlan * 0.4) grip = rot(0, hh + armPlan);
    return { corners, rotate: hold(grip), centre: { x: cx, y: cy } };
}

/**
 * The selection frame and its grips — preview only.
 *
 * Drawn straight onto the preview context AFTER compose, never into the plan:
 * the export goes through Exporter.render with the same plan and cannot pick
 * these up, so what you download is the picture and not the picture plus a set
 * of blue dots. (A decoration living in the plan is exactly how tools ship
 * handles into customers' files.)
 */
function drawHandles(plan, k) {
    if (S.mode !== 'overlay' || !plan.placements.length) return;
    const p = plan.placements.find((q) => q.i === S.selected);
    if (!p) return;
    const g = gripsFor(p, plan);
    const accent = getComputedStyle(document.documentElement)
        .getPropertyValue('--lg-accent-solid').trim() || '#7aa7ff';
    if (brushOn()) { drawBrushRing(plan, k, g, accent); return; }

    ctx.save();
    ctx.setTransform(k, 0, 0, k, 0, 0);
    ctx.lineWidth = 1.5 / k;
    ctx.strokeStyle = accent;
    ctx.setLineDash([6 / k, 4 / k]);
    ctx.beginPath();
    ctx.moveTo(g.corners[0].x, g.corners[0].y);
    for (let i = 1; i < 4; i++) ctx.lineTo(g.corners[i].x, g.corners[i].y);
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // Arm to the rotate grip.
    const topMid = { x: (g.corners[0].x + g.corners[1].x) / 2, y: (g.corners[0].y + g.corners[1].y) / 2 };
    ctx.beginPath(); ctx.moveTo(topMid.x, topMid.y); ctx.lineTo(g.rotate.x, g.rotate.y); ctx.stroke();

    const r = HANDLE / Math.max(0.0001, view.scale);
    const dot = (pt, fill) => {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
        ctx.fillStyle = fill; ctx.fill();
        ctx.lineWidth = 1.5 / k; ctx.strokeStyle = '#fff'; ctx.stroke();
    };
    g.corners.forEach((c) => dot(c, accent));
    dot(g.rotate, '#fff');
    ctx.restore();

    if (snapGuides.length) {
        ctx.save();
        ctx.setTransform(k, 0, 0, k, 0, 0);
        ctx.strokeStyle = accent; ctx.lineWidth = 1 / k; ctx.setLineDash([4 / k, 4 / k]);
        for (const gd of snapGuides) {
            ctx.beginPath();
            if (gd.v != null) { ctx.moveTo(gd.v, 0); ctx.lineTo(gd.v, plan.height); }
            else { ctx.moveTo(0, gd.h); ctx.lineTo(plan.width, gd.h); }
            ctx.stroke();
        }
        ctx.restore();
    }
}

let snapGuides = [];

/* ── hidden parts: the brush, the soft edge, the cut-out ─────────────────
   Farid 28.10.2026: «самому наложить одно фото поверх другого и самому
   настроить, какие части будут прозрачные» — by hand, not only through a
   model. The mask itself and its drawing are layer-mask.js; here is only what
   the page does with a pointer and the panel. Everything acts on the SELECTED
   layer; the strip below the preview picks it. */

const brush = { tool: 'move', size: 40, soft: 50 };   // size — diameter in CSS px; soft — %
let brushAt = null;            // the plan point under the pointer while a brush is chosen
let stroke = null;             // the stroke in progress: { it, p, st, last }
let maskUndo = [];             // undo patches, newest last
const MASK_UNDO_MAX = 30;
const maskStats = { strokes: 0, cutouts: 0 };
let cutting = false;

function brushOn() { return S.mode === 'overlay' && brush.tool !== 'move' && S.items.length > 0; }

function maskOpts() {
    return { deviceMemory: navigator.deviceMemory || 0, limits: Exporter.canvasLimits() };
}

/**
 * «Слоями» starts every photo above the base at 50 % — and a half-transparent
 * layer makes erased parts look like the rest of it. The first thing done to
 * the hidden parts lifts that automatic 50 % to a full photo; an opacity the
 * person set by hand stays theirs.
 */
function opaqueForMask(it) {
    if (!it || it.opacityByHand || it.opacity !== 0.5) return;
    it.opacity = 1;
    syncSelectedUI();
}

/** The brush's radius on the PLAN: a CSS-pixel brush keeps its size on screen whatever the zoom. */
function brushRadiusPlan() { return (brush.size / 2) / Math.max(0.0001, view.scale); }

/**
 * The bottom photo has nothing under it — what people erase is a photo ON TOP.
 * Choosing a brush with the bottom one selected moves the selection up once,
 * unless the bottom one already carries hidden parts (then it was meant).
 */
function pickMaskLayer() {
    if (S.items.length > 1 && S.selected === 0 && !LayerMask.hasMask(S.items[0])) {
        S.selected = S.items.length - 1;
        renderStrip();
        syncSelectedUI();
    }
}

function drawBrushRing(plan, k, g, accent) {
    ctx.save();
    ctx.setTransform(k, 0, 0, k, 0, 0);
    // the frame of the layer the brush works on — without grips: they do nothing now
    ctx.lineWidth = 1.5 / k;
    ctx.strokeStyle = accent;
    ctx.setLineDash([6 / k, 4 / k]);
    ctx.beginPath();
    ctx.moveTo(g.corners[0].x, g.corners[0].y);
    for (let i = 1; i < 4; i++) ctx.lineTo(g.corners[i].x, g.corners[i].y);
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);
    if (brushAt) {
        // two rings, dark under light: the ring has to read on any photo
        ctx.beginPath();
        ctx.arc(brushAt.x, brushAt.y, brushRadiusPlan(), 0, Math.PI * 2);
        ctx.lineWidth = 3 / k; ctx.strokeStyle = 'rgba(0,0,0,.55)'; ctx.stroke();
        ctx.lineWidth = 1.5 / k; ctx.strokeStyle = '#fff'; ctx.stroke();
    }
    ctx.restore();
}

/** A plan point → the mask's pixels and the brush radius there. */
function maskPoint(p, it, x, y) {
    const q = LayerMask.planToSource(p, x, y);
    if (!q) return null;
    const k = LayerMask.maskK(it);
    return { x: q.x * k, y: q.y * k, r: Math.max(0.5, brushRadiusPlan() * q.perPlan * k) };
}

function startStroke(pt) {
    const it = S.items[S.selected];
    const p = lastPlan && lastPlan.placements.find((q) => q.i === S.selected);
    if (!it || !p) return false;
    LayerMask.ensureMask(it, maskOpts());
    opaqueForMask(it);
    const mp = maskPoint(p, it, pt.x, pt.y);
    if (!mp) return false;
    stroke = { it, p, st: LayerMask.beginStroke(it), last: mp };
    paintTo(mp);
    return true;
}

function paintTo(mp) {
    // softness 0 % is a hard edge, 100 % fades from the centre out
    const hardness = Math.max(0, Math.min(0.999, 1 - brush.soft / 100));
    LayerMask.strokeTo(stroke.it, stroke.st, stroke.last.x, stroke.last.y, mp.x, mp.y, mp.r, hardness,
        brush.tool === 'erase' ? 'sub' : 'add');
    stroke.last = mp;
    scheduleRender();
}

function finishStroke() {
    if (!stroke) return;
    const patch = LayerMask.endStroke(stroke.it, stroke.st);
    if (patch) { pushMaskUndo(patch); maskStats.strokes++; }
    stroke = null;
    syncMaskUI();
    draftTouch(true);
}

function pushMaskUndo(patch) {
    maskUndo.push(patch);
    if (maskUndo.length > MASK_UNDO_MAX) maskUndo.shift();
}

/** One step back — strokes and whole-mask changes alike. → whether anything was undone. */
function undoMask() {
    while (maskUndo.length) {
        const patch = maskUndo.pop();
        const it = S.items.find((x) => x.id === patch.id);
        if (!it) continue;                    // that photo is gone; its steps go with it
        LayerMask.applyPatch(it, patch, 'before');
        if (patch.fadeBefore) it.fade = Object.assign({}, patch.fadeBefore);
        if (!LayerMask.hasMask(it)) LayerMask.dropCache(it);
        ae('feature_use', { f: 'mask_undo' });
        scheduleRender();
        syncMaskUI();
        draftTouch(true);
        return true;
    }
    syncMaskUI();
    return false;
}

function maskSnapshot(it) {
    return it.mask ? { w: it.mask.w, h: it.mask.h, data: it.mask.data.slice() } : null;
}

/** Invert or forget the selected layer's hidden parts — one undo step each. */
function maskOp(kind) {
    if (kind === 'invert') pickMaskLayer();
    const it = S.items[S.selected];
    if (!it) return;
    const before = maskSnapshot(it);
    const fadeBefore = Object.assign({}, it.fade);
    if (kind === 'invert') LayerMask.invert(it, maskOpts());
    else { LayerMask.clearMask(it); it.fade = { side: 'none', size: (it.fade && it.fade.size) || 0.25 }; }
    const patch = LayerMask.snapshotPatch(it, before);
    patch.fadeBefore = fadeBefore;
    pushMaskUndo(patch);
    ae('feature_use', { f: 'mask_' + kind });
    scheduleRender();
    syncMaskUI();
    draftTouch(true);
}

/**
 * «Cut out the person» — the RVM matte /photo-editor uses (photo-editor/ops),
 * loaded on the first press only. It comes back as this layer's MASK: the brush
 * can then bring back a hand the model missed or wipe a piece of background it
 * kept. Edge decontamination stays off — it rewrites the photo's own pixels,
 * and «Мои работы» keeps the photo as its bytes.
 */
async function cutOut() {
    if (cutting) return;
    pickMaskLayer();
    const it = S.items[S.selected];
    const btn = $('miCutout');
    if (!it || !btn) return;
    cutting = true;
    const label = btn.textContent;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = t('cut_busy', { pct: 0 });
    const t0 = Date.now();
    let ok = 0, why = '', cov = 0;
    try {
        const ops = await import('../photo-editor/ops/index.js');
        const op = ops.OPS && ops.OPS['bg-remove'];
        if (!op) throw new Error('engine_unavailable');
        const k = it.mask ? LayerMask.maskK(it) : LayerMask.maskScale(it.w, it.h, maskOpts());
        const c = document.createElement('canvas');
        c.width = it.mask ? it.mask.w : Math.max(1, Math.round(it.w * k));
        c.height = it.mask ? it.mask.h : Math.max(1, Math.round(it.h * k));
        c.getContext('2d').drawImage(it.d.drawable, 0, 0, it.w, it.h, 0, 0, c.width, c.height);
        const res = await op.run(c, { subject: 'people', decontaminate: false }, {
            progress: (f) => { btn.textContent = t('cut_busy', { pct: Math.max(0, Math.min(99, Math.round((f || 0) * 100))) }); },
        });
        if (!res || !res.mask || (res.info && res.info.subject === false)) {
            why = 'no_subject';
            if (window.__toast) window.__toast(t('cut_none'), 'info');
        } else {
            const before = maskSnapshot(it);
            LayerMask.adoptMatte(it, res.mask, maskOpts());
            opaqueForMask(it);
            pushMaskUndo(LayerMask.snapshotPatch(it, before));
            cov = 1 - LayerMask.hiddenShare(it);
            ok = 1;
            maskStats.cutouts++;
            draftTouch(true);
            if (window.__announce) window.__announce(t('cut_done'));
        }
    } catch (err) {
        why = String((err && err.message) || err).slice(0, 60);
        if (window.__handleError) window.__handleError(err, 'mask_cutout', { say: t('cut_failed') });
        else if (window.__toast) window.__toast(t('cut_failed'), 'error');
    } finally {
        cutting = false;
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
        ae('feature_use', { f: 'mask_cutout', ok, ms: Date.now() - t0, why, cov: Math.round(cov * 100) });
        scheduleRender();
        syncMaskUI();
    }
}

function saveBrush() {
    try { if (window.__toolSettings) window.__toolSettings.save(SLUG, 'brush', { size: brush.size, soft: brush.soft }); } catch (_) {}
}
function loadBrush() {
    let b = null;
    try { b = window.__toolSettings ? window.__toolSettings.load(SLUG, 'brush', null) : null; } catch (_) { b = null; }
    if (b && typeof b === 'object') {
        if (b.size >= 4 && b.size <= 300) brush.size = Math.round(b.size);
        if (b.soft >= 0 && b.soft <= 100) brush.soft = Math.round(b.soft);
    }
}

/** The panel follows the page: Layers only, the selected layer's values, what can be undone. */
function syncMaskUI() {
    if (!$('miMaskPanel')) return;          // yesterday's markup: no panel, nothing to sync
    const on = S.mode === 'overlay' && S.items.length > 0;
    show('miMaskPanel', on);
    const box = $('miCanvasBox');
    if (box) box.classList.toggle('brush', on && brush.tool !== 'move');
    if (!on) return;
    const it = S.items[S.selected];
    Array.from(document.querySelectorAll('#miTool button')).forEach((b) => {
        const v = b.dataset.v === brush.tool;
        b.classList.toggle('on', v);
        b.setAttribute('aria-pressed', String(v));
    });
    show('miBrushFields', brush.tool !== 'move');
    const set = (id, v) => { const e = $(id); if (e) e.value = v; };
    const txt = (id, v) => { const e = $(id); if (e) e.textContent = v; };
    set('miBrushSize', brush.size); txt('miBrushSizeV', brush.size);
    set('miBrushSoft', brush.soft); txt('miBrushSoftV', brush.soft + '%');
    const fade = (it && it.fade) || { side: 'none', size: 0.25 };
    set('miFade', fade.side);
    show('miFldFadeSize', fade.side !== 'none');
    set('miFadeSize', Math.round(fade.size * 100)); txt('miFadeSizeV', Math.round(fade.size * 100) + '%');
    const undo = $('miMaskUndo');
    if (undo) undo.disabled = !maskUndo.some((pt) => S.items.some((x) => x.id === pt.id));
    const reset = $('miMaskReset');
    if (reset) reset.disabled = !(it && LayerMask.hasMask(it));
    const state = $('miMaskState');
    if (state && it) {
        state.textContent = it.mask
            ? t('mask_state', { n: S.selected + 1, pct: Math.round(LayerMask.hiddenShare(it) * 100) })
            : t('mask_layer', { n: S.selected + 1 });
    }
}

function wireMask() {
    if (!$('miMaskPanel')) return;
    loadBrush();
    Array.from(document.querySelectorAll('#miTool button')).forEach((b) => b.addEventListener('click', () => {
        brush.tool = b.dataset.v;
        if (brush.tool !== 'move') pickMaskLayer();
        brushAt = null;
        ae('feature_use', { f: 'mask_tool', v: brush.tool });
        syncMaskUI();
        scheduleRender();
    }));
    const on = (id, ev, fn) => { const e = $(id); if (e) e.addEventListener(ev, fn); };
    on('miBrushSize', 'input', (e) => { brush.size = +e.target.value || 40; syncMaskUI(); scheduleRender(); });
    on('miBrushSize', 'change', saveBrush);
    on('miBrushSoft', 'input', (e) => { brush.soft = +e.target.value; syncMaskUI(); });
    on('miBrushSoft', 'change', saveBrush);
    on('miFade', 'change', (e) => {
        pickMaskLayer();
        const it = S.items[S.selected];
        if (!it) return;
        const side = LayerMask.FADE_SIDES.includes(e.target.value) ? e.target.value : 'none';
        it.fade = { side, size: (it.fade && it.fade.size) || 0.25 };
        if (side !== 'none') opaqueForMask(it);
        ae('feature_use', { f: 'mask_fade', side });
        syncMaskUI();
        scheduleRender();
        draftTouch(true);
    });
    on('miFadeSize', 'input', (e) => {
        const it = S.items[S.selected];
        if (!it || !it.fade) return;
        it.fade = { side: it.fade.side, size: Math.max(0.02, Math.min(0.6, (+e.target.value || 25) / 100)) };
        syncMaskUI();
        scheduleRender();
        draftTouch(false);
    });
    on('miFadeSize', 'change', () => draftTouch(true));
    on('miCutout', 'click', cutOut);
    on('miMaskInvert', 'click', () => maskOp('invert'));
    on('miMaskUndo', 'click', undoMask);
    on('miMaskReset', 'click', () => maskOp('reset'));
}

/**
 * Snap a dragged layer to the canvas edges and centre lines.
 *
 * Threshold is in SCREEN pixels converted back to plan units, so the pull feels
 * the same whether the preview is showing a 600 px thumbnail or a 4 000 px
 * composition — a fixed plan-space threshold would be unusable at one end and
 * invisible at the other.
 */
function snap(it, p, plan) {
    snapGuides = [];
    const tol = 7 / Math.max(0.0001, view.scale);
    const w = p.w, h = p.h;
    const targetsX = [0, (plan.width - w) / 2, plan.width - w];
    const targetsY = [0, (plan.height - h) / 2, plan.height - h];
    const lines = [0, plan.width / 2, plan.width];
    const linesY = [0, plan.height / 2, plan.height];
    for (let i = 0; i < 3; i++) {
        if (Math.abs(p.x - targetsX[i]) < tol) { it.x += (targetsX[i] - p.x) / (plan.scale || 1); snapGuides.push({ v: lines[i] }); break; }
    }
    for (let i = 0; i < 3; i++) {
        if (Math.abs(p.y - targetsY[i]) < tol) { it.y += (targetsY[i] - p.y) / (plan.scale || 1); snapGuides.push({ h: linesY[i] }); break; }
    }
}

/** Rough output weight so the user is not surprised by a 40 MB download. */
function estimateBytes(plan) {
    const px = plan.width * plan.height;
    if (S.format === 'jpeg') return px * (0.08 + 0.5 * Math.pow(S.quality / 100, 3));
    if (S.format === 'webp') return px * (0.05 + 0.35 * Math.pow(S.quality / 100, 3));
    return px * 0.9;                                   // PNG, screenshot-ish content
}

function updateMeta(plan) {
    $('miOutSize').textContent = plan.width + ' × ' + plan.height;
    $('miOutWeight').textContent = '≈ ' + fmtBytes(estimateBytes(plan));

    // Transparency that the chosen format cannot carry.
    const hasAlpha = Compose.planHasAlpha(plan, S.items);
    const keeps = (S.format === 'png' || S.format === 'webp');
    if (hasAlpha && !keeps) {
        $('miAlphaText').textContent = t('alpha_warn');
        show('miAlphaNote', true);
    } else show('miAlphaNote', false);

    // Anything being interpolated above 1:1 — the honest version of "resize
    // smaller to larger", which every competitor does silently.
    const worst = plan.placements.reduce((m, p) => Math.max(m, p.upscale || 1), 1);
    if (worst > 1.15) {
        $('miUpscaleText').textContent = t('upscale_warn', { x: worst.toFixed(1) });
        show('miUpscaleNote', true);
    } else show('miUpscaleNote', false);

    // Two separate ceilings, and conflating them would mislead:
    //   • the device's CANVAS limit → the streaming encoder takes over, fine;
    //   • the BUDGET → nothing can produce this, and trying freezes the tab.
    const limit = Exporter.canvasLimits();
    const big = !Exporter.fitsOnCanvas(plan, limit);
    const mp = plan.megapixels;
    const overBudget = mp > Exporter.BUDGET_MAX_MP;
    const heavy = mp > Exporter.BUDGET_WARN_MP;

    const bigNote = $('miBigNote');
    if (overBudget) {
        $('miBigText').textContent = t('too_big_note', { w: plan.width, h: plan.height, mp: Math.round(mp) });
        bigNote.classList.remove('mi-hidden');
        bigNote.classList.add('warn');
        show('miBigFix', true);
    } else if (big || heavy) {
        $('miBigText').textContent = big
            ? t('big_note', { w: plan.width, h: plan.height, mp: (limit.area / 1e6).toFixed(0), side: limit.side })
            : t('heavy_note', { mp: Math.round(mp) });
        bigNote.classList.remove('mi-hidden');
        bigNote.classList.remove('warn');
        show('miBigFix', false);
    } else {
        bigNote.classList.add('mi-hidden');
        show('miBigFix', false);
    }
    $('miPathNote').textContent = big && !overBudget ? t('path_streamed') : '';

    // Refusing at the button is the honest place: the user sees WHY before
    // clicking, instead of after a minute of frozen tab.
    $('miDownload').disabled = overBudget || S.busy;
    $('miCopy').disabled = overBudget || S.busy;
    if (overBudget && !S._budgetReported) {
        S._budgetReported = true;
        ae('feature_use', { f: 'plan_over_budget', mp: Math.round(mp), w: plan.width, h: plan.height, mode: S.mode });
    }
    if (!overBudget) S._budgetReported = false;
}

/* ── thumbnails ───────────────────────────────────────────────────────── */

/**
 * The strip is order + selection, and nothing else.
 *
 * It used to carry six buttons per card (rotate, flip, remove, left, right,
 * index) at 28 px each. Rotate and flip moved to the one panel that is about
 * the selected photo, and the arrows went with them: reordering is dragging on
 * a pointer device and Alt+arrows from the keyboard, which is also the only
 * route a screen-reader user ever had.
 */
function renderStrip() {
    const strip = $('miStrip');
    strip.textContent = '';
    S.items.forEach((it, i) => {
        const el = document.createElement('button');
        el.type = 'button';
        el.className = 'mi-thumb' + (i === S.selected ? ' sel' : '');
        el.draggable = true;
        el.dataset.i = String(i);
        el.setAttribute('role', 'option');
        el.setAttribute('aria-selected', String(i === S.selected));
        el.setAttribute('aria-label', t('layer_selected', { n: i + 1 }));

        const img = document.createElement('canvas');
        img.className = 'mi-thumb-img' + (it.hasAlpha ? ' has-alpha' : '');
        img.width = 180; img.height = 140;
        const c = img.getContext('2d');
        const k = Math.min(180 / it.w, 140 / it.h);
        c.drawImage(it.d.drawable, (180 - it.w * k) / 2, (140 - it.h * k) / 2, it.w * k, it.h * k);
        el.appendChild(img);

        if (it.d.animated) {
            const b = document.createElement('span');
            b.className = 'mi-flag anim';
            b.textContent = 'GIF';
            b.title = t('badge_animated');
            el.appendChild(b);
        } else if (it.hasAlpha) {
            const b = document.createElement('span');
            b.className = 'mi-flag';
            b.textContent = 'α';
            b.title = t('badge_alpha');
            el.appendChild(b);
        }

        const n = document.createElement('span');
        n.className = 'mi-thumb-n';
        n.textContent = String(i + 1);
        el.appendChild(n);

        const x = document.createElement('button');
        x.type = 'button'; x.className = 'mi-x';
        x.title = t('it_remove'); x.setAttribute('aria-label', t('it_remove') + ' ' + (i + 1));
        x.textContent = '✕';
        x.addEventListener('click', (e) => { e.stopPropagation(); removeItem(i); });
        el.appendChild(x);

        /* Order buttons, and they are not a duplicate of dragging.
           `wireDrag` below is HTML5 drag-and-drop, which does not fire on touch
           at all — so without these there is NO way to reorder on a phone, and
           a phone is where screenshots come from. Alt+arrows covers keyboards,
           these cover fingers. */
        const ord = document.createElement('span');
        ord.className = 'mi-thumb-order';
        [[-1, '‹', 'it_move_left'], [1, '›', 'it_move_right']].forEach(([dir, glyph, key]) => {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'mi-ord';
            b.textContent = glyph;
            b.title = t(key);
            b.setAttribute('aria-label', t(key) + ' ' + (i + 1));
            // The ends are shown but disabled, so the control does not reflow
            // under the finger as an image travels along the strip.
            b.disabled = (dir < 0 && i === 0) || (dir > 0 && i === S.items.length - 1);
            b.addEventListener('click', (e) => { e.stopPropagation(); moveItem(i, dir, 'button'); });
            ord.appendChild(b);
        });
        el.appendChild(ord);

        el.addEventListener('click', () => selectItem(i));
        // Alt+arrows reorder from the keyboard — the strip is a listbox, so the
        // bare arrows have to keep moving the selection.
        el.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                const dir = (e.key === 'ArrowRight') ? 1 : -1;
                if (e.altKey) moveItem(i, dir);
                else selectItem(Math.min(S.items.length - 1, Math.max(0, i + dir)), true);
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                e.preventDefault(); removeItem(i);
            }
        });
        wireDrag(el, i);
        strip.appendChild(el);
    });

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'mi-add';
    const plus = document.createElement('b');
    plus.textContent = '+'; plus.setAttribute('aria-hidden', 'true');
    add.appendChild(plus);
    add.appendChild(document.createTextNode(t('add_more')));
    add.addEventListener('click', () => {
        const input = document.querySelector('[data-uploader-for="miDrop"]');
        if (input) input.click();
    });
    strip.appendChild(add);
    // the upload zone's row is the same list said once more (syncZone)
    syncZone();
}

function selectItem(i, focus) {
    S.selected = i;
    renderStrip();
    syncSelectedUI();
    scheduleRender();
    draftTouch(false);
    if (focus) {
        const el = document.querySelector(`.mi-thumb[data-i="${i}"]`);
        // block:'nearest' only — it is a no-op when the thumbnail is already
        // visible, and never moves the page.
        if (el) { el.focus(); el.scrollIntoView({ block: 'nearest', inline: 'nearest' }); }
    }
}

let dragFrom = -1;
function wireDrag(el, i) {
    el.addEventListener('dragstart', (e) => {
        dragFrom = i; el.classList.add('dragging');
        try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(i)); } catch (_) {}
    });
    el.addEventListener('dragend', () => {
        el.classList.remove('dragging');
        document.querySelectorAll('.mi-thumb').forEach((n) => n.classList.remove('drop-before', 'drop-after'));
        dragFrom = -1;
    });
    el.addEventListener('dragover', (e) => {
        if (dragFrom < 0) return;
        e.preventDefault();
        const r = el.getBoundingClientRect();
        const after = (e.clientX - r.left) > r.width / 2;
        el.classList.toggle('drop-after', after);
        el.classList.toggle('drop-before', !after);
    });
    el.addEventListener('dragleave', () => el.classList.remove('drop-before', 'drop-after'));
    el.addEventListener('drop', (e) => {
        e.preventDefault();
        if (dragFrom < 0 || dragFrom === i) return;
        const r = el.getBoundingClientRect();
        let to = (e.clientX - r.left) > r.width / 2 ? i + 1 : i;
        const moved = S.items.splice(dragFrom, 1)[0];
        if (dragFrom < to) to--;
        S.items.splice(to, 0, moved);
        S.selected = to;
        dropSeams();
        // `how` on every reorder, or the counts cannot answer the question they
        // exist for: dragging is desktop-only (HTML5 DnD does not fire on
        // touch), so an unlabelled total hides whether the buttons are the only
        // door anyone on a phone has.
        ae('feature_use', { f: 'reorder', how: 'drag' });
        refresh();
        maybeOffer();
        draftTouch(true);
    });
}

/* ── files ────────────────────────────────────────────────────────────── */

async function addFiles(list, source) {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length) return;
    adding++;
    // …and whatever the pick came to — pictures on the page, none that opened,
    // a work replaced meanwhile — the zone goes back to saying what the page holds
    try { await addPicked(files, source); } finally { adding--; syncZone(); }
}

async function addPicked(files, source) {
    // Picked while the page's work was being replaced — a saved work coming back,
    // or «start over» still filing the old one away: wait for it, then join what
    // the page holds, instead of landing among pictures about to go.
    while (restoring || resetting) {
        waitingPicks++;
        try { await (restoring || resetting); } catch (_) {} finally { waitingPicks--; }
    }
    const gen = workGen;
    if (S.items.length + files.length > MAX_ITEMS) {
        window.__toast && window.__toast(t('too_many', { n: MAX_ITEMS }), 'warning');
    }
    const room = Math.max(0, MAX_ITEMS - S.items.length);
    let added = 0, failed = 0;
    for (const f of files.slice(0, room)) {
        try {
            const d = await decodeFile(f, { emit: (e, p) => ae('feature_use', Object.assign({ f: e }, p)) });
            // «Start over» or another work opened while this one decoded: it
            // belonged to the work that is gone.
            if (gen !== workGen) { release(d); return; }
            S.items.push(newItem(d));
            added++;
            if (d.animated) {
                window.__toast && window.__toast(t('animated_first_frame', { name: d.name }), 'info');
            }
        } catch (err) {
            failed++;
            try {
                window.__aeToolError && window.__aeToolError({
                    slug: SLUG, error_type: (err && err.code) || 'decode_failed', msg: (err && err.message) || '',
                });
            } catch (_) {}
        }
    }
    if (failed) window.__handleError
        ? window.__handleError(new Error(t('err_decode', { n: failed })), SLUG)
        : window.__toast && window.__toast(t('err_decode', { n: failed }), 'error');
    if (!added) return;

    ae('feature_use', {
        f: 'file_picked', count: added, failed, source: source || 'pick', total: S.items.length,
        alpha: S.items.filter((i) => i.hasAlpha).length,
        animated: S.items.filter((i) => i.d.animated).length,
        same_size: new Set(S.items.map((i) => i.w + 'x' + i.h)).size === 1 ? 1 : 0,
        mp: Math.round(S.items.reduce((s, i) => s + i.w * i.h, 0) / 1e6),
    });
    try { window.__ae && window.__ae('tool_funnel', { slug: SLUG, stage: 'file_select', count: S.items.length }); } catch (_) {}
    stage('configure', { n: S.items.length });

    // Transparent input → keep it transparent. Deciding this once, on first load,
    // is what makes "merge two PNG logos" produce a PNG with alpha instead of a
    // surprise white slab.
    if (S.items.some((i) => i.hasAlpha) && S.bgMode !== 'color') setBg('transparent');

    dropSeams();
    showWorkspace();
    refresh();
    maybeOffer();
    // One change per picture: two pictures picked at once are already a merge
    // worth reopening after a crash (the controller reopens from two changes).
    for (let k = 0; k < added; k++) draftTouch(true);
}

/* ── contextual offers ────────────────────────────────────────────────── */

/**
 * The two presets that survived, offered instead of listed.
 *
 * A five-chip "quick start" bar asked the same question as the mode segment
 * right below it. What a preset is actually for is the case where the software
 * can see something the user has not said yet — these are those two cases, and
 * each is offered only when the loaded files make it true.
 *
 * Offered, never applied. Silently rearranging someone's images because the
 * software thinks it knows better is how a clever feature becomes a complaint.
 */
function maybeOffer() {
    maybeOfferStitch();
    // Two photos side by side is almost always a comparison, and the panel that
    // does it says so on its own line. It is not ALSO offered in a bar over the
    // canvas: on a wide screen both are on screen at once, and asking the same
    // question twice in one view is the thing this rebuild set out to remove.
    const panel = $('miComparePanel');
    if (compareApplies() && !S.compareOn && !panel.classList.contains('mi-hidden') && !S._compareFlashed) {
        S._compareFlashed = true;
        panel.classList.add('tc-flash');
        setTimeout(() => panel.classList.remove('tc-flash'), 600);
        ae('feature_use', { f: 'compare_offered' });
    }
}

/**
 * Offer to fold the repeats — only on PROOF that these are captures of one page.
 *
 * The page speaks first here, unasked, and what it proposes cuts rows out of
 * the person's pictures. So the bar is not "this could be a capture" but "two
 * of these frames demonstrably show the same rows of one page" — a shared band
 * found, and found in content that could have disagreed (Stitch.captureVerdict).
 *
 * Until 2026-10-07 the offer also went out on fixed bars alone. Measured, RU
 * production, 16.09–06.10: 1 008 offers, 939 of them (93 %) without one shared
 * row; 64 % of the sessions that loaded two or more pictures of one size got
 * the offer; 226 of the 240 presses of its button were on such an offer, and
 * what they did was cut the tops and bottoms off photographs and scans. On a
 * labelled corpus the old rule offered on 57 % of 939 sets that are not
 * captures at all; this one on none of them, and still on every one of the 28
 * sets of real overlapping captures (docs/merge-images-stitch.md).
 */
function maybeOfferStitch() {
    const note = $('miStitchOffer');
    if (!note) return;
    note.classList.add('mi-hidden');
    S._offer = null;
    if (S.autoStitch || S.items.length < 2) return;
    // «Leave as is» was an answer about THESE frames in THIS order.
    if (S._offerDeclined && S._offerDeclined === setKey()) return;
    // Scroll captures come off one screen, so they share exact dimensions. Any
    // other mix is a collage, not a strip, and must not be second-guessed.
    const first = S.items[0];
    if (!S.items.every((i) => i.w === first.w && i.h === first.h)) return;

    let res;
    try {
        /* Measured on the FILES, not on the current mode. Whether five frames are
           a scrolled page is a fact about them; the mode is what the user has
           picked so far, and gating on it meant the offer only ever reached
           people who had already worked out that they wanted a vertical strip —
           which is precisely the audience that does not need to be told. The
           tool opens on "side by side", so before this the offer was invisible
           on a fresh page load. Accepting it switches the mode, visibly. */
        res = measureColumn();
    } catch (_) { return; }
    if (!res.verdict.capture) return;

    const found = res.seams.filter((s) => s.auto).length;
    // The offer is about these frames in this order; a stale one is not acted on.
    S._offer = { key: setKey() };
    $('miStitchOfferText').textContent = t('stitch_offer', { n: found, total: res.seams.length });
    note.classList.remove('mi-hidden');
    ae('feature_use', {
        f: 'stitch_offered', found, proven: res.verdict.proven, total: res.seams.length,
        chrome: res.chrome.top + res.chrome.bottom,
    });
}

function acceptStitchOffer() {
    if (!S._offer || S._offer.key !== setKey()) { maybeOfferStitch(); return; }
    // Stitching IS the vertical strip, so accepting the offer moves the mode
    // there. Doing it here rather than in the detector keeps the rule of this
    // panel intact: offered, never applied behind the user's back.
    if (S.mode !== 'vertical') {
        S.mode = 'vertical';
        applyModeDefaults('vertical');
        syncModeUI();       // the segment has to move too, or the mode lies
        ae('feature_use', { f: 'mode', mode: 'vertical', via: 'stitch_offer' });
    }
    S.autoStitch = true;
    $('miAutoStitch').checked = true;
    $('miStitchDrawer').open = true;
    show('miStitchOffer', false);
    S._offer = null;
    S._preset = 'screenshot';
    // One door for measuring and applying — the same one the switch uses, so
    // what was offered and what gets applied cannot be two different answers.
    const res = runAutoStitch();
    ae('feature_use', {
        f: 'stitch_offer_accepted',
        found: S.seams.filter((s) => s.auto).length,
        proven: res ? res.verdict.proven : 0,
    });
    scheduleRender();
    draftTouch(true);
}

function enableCompare(fromOffer) {
    S.compareOn = true;
    S._preset = 'beforeafter';
    if (!S.labelA) S.labelA = t('cap_before');
    if (!S.labelB) S.labelB = t('cap_after');
    $('miCompareOn').checked = true;
    $('miLabelA').value = S.labelA;
    $('miLabelB').value = S.labelB;
    ae('feature_use', { f: 'compare_on', via: fromOffer ? 'offer' : 'switch' });
    syncModeUI();
    scheduleRender();
}

/* ── remembered settings ──────────────────────────────────────────────── */

// Only preferences, never content: how someone likes their merges laid out is
// stable across visits, which images they merged is not.
const REMEMBERED = ['mode', 'columns', 'fit', 'matchTarget', 'align', 'gap', 'padding',
    'radius', 'bgMode', 'bgColor', 'borderW', 'borderC', 'format', 'quality', 'maxOut',
    'harmonize', 'harmonizeRef'];

function saveSettings() {
    if (!window.__toolSettings) return;
    const out = {};
    REMEMBERED.forEach((k) => { out[k] = S[k]; });
    try { window.__toolSettings.save(SLUG, 'prefs', out); } catch (_) {}
}

function loadSettings() {
    if (!window.__toolSettings) return;
    let saved;
    try { saved = window.__toolSettings.load(SLUG, 'prefs', null); } catch (_) { return; }
    if (!saved || typeof saved !== 'object') return;
    REMEMBERED.forEach((k) => {
        if (saved[k] !== undefined && saved[k] !== null) S[k] = saved[k];
    });
}

function moveItem(i, dir, how) {
    const j = i + dir;
    if (j < 0 || j >= S.items.length) return;
    const [m] = S.items.splice(i, 1);
    S.items.splice(j, 0, m);
    S.selected = j;
    dropSeams();
    ae('feature_use', { f: 'reorder', how: how || 'keyboard' });
    refresh();
    maybeOffer();
    draftTouch(true);
    const el = document.querySelector(`.mi-thumb[data-i="${j}"]`);
    if (el) el.focus();
}

/**
 * Removal is undoable. A misplaced tap on a small button costs a re-upload
 * otherwise — and on a phone, where the files came from a share sheet, "just
 * add it again" can mean going back through another app.
 */
let lastRemoved = null;

/* Captions are on while any picture has one — typing turns them on; removing the
   only captioned picture turns them off again (until 01.10.2026 the band stayed,
   empty, over every picture), and «undo» brings them back with it. */
function syncCaptions() {
    const any = S.items.some((x) => x.caption);
    if (any !== S.captionsOn) { S.captionsOn = any; show('miCapStyle', any); }
}

/**
 * The page is empty: whatever comes next, it is not the frames the stitch was
 * about. The layout preferences stay (they are how the person likes a merge);
 * the stitch is a statement about particular pictures and goes with them.
 *
 * It did not. In the reported session (#1840) the person removed both photos
 * and dropped them again five times over, in both orders — and each time the
 * new pair inherited «stitch on» and the 425 px crop measured for the first
 * one, with the offer that had started it no longer on screen to explain why.
 */
function forgetStitch() {
    S.autoStitch = false;
    S.trimTop = 0;
    S.trimBottom = 0;
    dropSeams();
    S._offer = null;
    S._offerDeclined = '';
    if (S._preset === 'screenshot') S._preset = '';
    $('miAutoStitch').checked = false;
    show('miStitchOffer', false);
    syncStitchUI();
}

function removeItem(i) {
    const it = S.items[i];
    if (!it) return;
    dropRemoved();                     // the one removed before this has no way back any more
    lastRemoved = { item: it, index: i };
    S.items.splice(i, 1);
    dropSeams();
    syncCaptions();
    if (S.selected >= S.items.length) S.selected = Math.max(0, S.items.length - 1);
    ae('feature_use', { f: 'remove_image', left: S.items.length });
    showUndo();
    draftTouch(true);
    if (!S.items.length) { forgetStitch(); hideWorkspace(); syncZone(); return; }
    refresh();
    maybeOffer();
}

function undoRemove() {
    if (!lastRemoved) return;
    S.items.splice(Math.min(lastRemoved.index, S.items.length), 0, lastRemoved.item);
    lastRemoved = null;
    dropSeams();
    syncCaptions();
    ae('feature_use', { f: 'undo_remove', left: S.items.length });
    hideUndo();
    showWorkspace();
    refresh();
    maybeOffer();
    draftTouch(true);
}

/**
 * «Removed — put back» is said where the person can reach it.
 *
 * With pictures left that is the stage, next to them. With none left the stage
 * is hidden — and the bar used to be shown INSIDE it: after the last picture's
 * × the way back existed for twelve seconds and could not be seen or pressed
 * (measured 07.10.2026: the bar's class said «shown», its box was 0 × 0). The
 * same line now also stands under the upload zone, for that one case.
 */
let undoTimer = 0;
function showUndo() {
    const empty = !S.items.length;
    show('miUndo', !empty);
    show('miUndoEmpty', empty);
    clearTimeout(undoTimer);
    undoTimer = setTimeout(hideUndo, 12000);
}
function hideUndo() {
    clearTimeout(undoTimer);
    show('miUndo', false);
    show('miUndoEmpty', false);
    dropRemoved();
}
/* The removed picture's way back is closed: let go of what it held. Nothing
   did — its object URL stayed registered until the tab was closed, one per
   picture ever removed. */
function dropRemoved() {
    if (lastRemoved) release(lastRemoved.item.d);
    lastRemoved = null;
}

function showWorkspace() { show('miWorkspace', true); }
function hideWorkspace() { show('miWorkspace', false); }

/**
 * The upload zone's row says what is on the PAGE.
 *
 * The zone collapses itself on a pick, with THAT pick's files, and nothing told
 * it what happened next. Measured 07.10.2026: two pictures and a third added —
 * the row named the third alone; one removed — unchanged; the LAST one removed —
 * «frame-01.png +1 · 95 KB · Другой файл» over an empty page (in the session of
 * #1840 the person emptied the page five times and was told each time that both
 * files were still there); a file that would not open, on an empty page — the
 * row named it as loaded.
 *
 * So the row is drawn from the list, wherever the list is drawn (renderStrip)
 * and where it stops being drawn at all (the last removal, a pick that came to
 * nothing). An empty page gets the open invitation back — except while a pick
 * is still decoding: that one is on its way here, and its row waits for it.
 * A zone that is open already is left alone: reopening is not free (it also
 * drops the zone's own message about a file it refused).
 */
function syncZone() {
    const zone = $('miDrop');
    if (!zone) return;
    try {
        if (S.items.length) {
            if (zone.__uploaderCollapse) zone.__uploaderCollapse(S.items.map((it) => it.d.file || { name: it.d.name, size: it.d.size }));
        } else if (!adding && zone.classList.contains('tu-collapsed') && zone.__uploaderRestore) zone.__uploaderRestore(true);
    } catch (_) {}
}

function refresh() {
    // (signatures are NOT dropped here any more: signatureFor() keeps each one
    // for as long as what it was read from stands)
    if (S.autoStitch && (S.mode === 'vertical') && S.items.length > 1) runAutoStitch();
    renderStrip();
    syncModeUI();
    syncStitchUI();
    syncSelectedUI();
    scheduleRender();
}

/* ── control wiring ───────────────────────────────────────────────────── */

function seg(id, key, after) {
    const box = $(id);
    box.addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (!b) return;
        Array.from(box.querySelectorAll('button')).forEach((x) => {
            x.setAttribute('aria-pressed', String(x === b));
            x.classList.toggle('on', x === b);
        });
        S[key] = b.dataset.v;
        if (after) after(S[key]);
        scheduleRender();
        draftTouch(true);
    });
}

function range(id, key, outId, fmt, after) {
    const el = $(id);
    el.addEventListener('input', () => {
        S[key] = +el.value;
        if (outId) $(outId).textContent = fmt ? fmt(+el.value) : el.value;
        if (after) after(+el.value);
        scheduleRender();
    });
}

function setBg(mode) {
    S.bgMode = mode;
    Array.from($('miBgMode').querySelectorAll('button')).forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.v === mode));
        b.classList.toggle('on', b.dataset.v === mode);
    });
    show('miFldBgColor', mode === 'color');
}

/**
 * What the "quick start" chips used to do, without the chips.
 *
 * The old page carried a five-preset bar above the mode tabs, and most of what
 * a preset added was three numbers: a grid of photos butted flush together
 * reads as a mistake, and a stack of perfectly coincident layers reads as one
 * image that failed to load its siblings. Removing the bar as a duplicate
 * control should not have removed that.
 *
 * ⚠️ Only values the user has NOT touched are set. `touched` is filled by the
 * controls themselves, so a deliberate gap of 0 survives every mode switch —
 * settings that quietly move under you are worse than settings that start at
 * zero.
 */
const touched = new Set();

function applyModeDefaults(mode) {
    const set = (key, value, el, out) => {
        if (touched.has(key) || S[key] !== DEFAULTS[key]) return;
        S[key] = value;
        if (el) $(el).value = value;
        if (out) $(out).textContent = value;
    };
    if (mode === 'grid') {
        set('gap', 8, 'miGap', 'miGapV');
        set('padding', 8, 'miPad', 'miPadV');
        set('radius', 12, 'miRadius', 'miRadiusV');
    } else if (mode === 'overlay') {
        // Cascade the layers so the result reads as several images.
        S.items.forEach((it, i) => {
            if (i && !it.x && !it.y) { it.x = Math.round(i * it.w * 0.12); it.y = Math.round(i * it.h * 0.12); }
        });
        // Half-opacity on everything above the base is what "overlay" means to
        // someone who just pressed it; a fully opaque top layer hides the rest.
        if (S.items.every((it) => it.opacity === 1)) {
            S.items.forEach((it, i) => { if (i) it.opacity = 0.5; });
        }
    }
    syncDrawerSubs();
}

function syncModeUI() {
    Array.from(document.querySelectorAll('.mi-mode'))
        .forEach((b) => b.setAttribute('aria-selected', String(b.dataset.mode === S.mode)));
    const isGrid = S.mode === 'grid';
    const isOverlay = S.mode === 'overlay';
    const isStrip = S.mode === 'vertical' || S.mode === 'horizontal';
    // Overlay hides every control in the arrangement panel (a free layer has no
    // fit, no target and no alignment), and a panel that is only a heading reads
    // as something failing to load.
    show('miLayoutPanel', !isOverlay);
    show('miFldColumns', isGrid);
    show('miStitchDrawer', isStrip && S.items.length > 1);
    show('miLayerFields', isOverlay);
    show('miFldFit', !isOverlay);
    show('miFldTarget', !isOverlay);
    show('miFldAlign', !isOverlay && !isGrid);
    show('miFldTargetPx', !isOverlay && S.matchTarget === 'custom');
    show('miFldBorderC', S.borderW > 0);
    show('miCapStyle', S.captionsOn);
    // The comparison panel exists only where a divider and two labels mean
    // something. Hiding it is not tidiness: shown for a six-photo grid it would
    // be a control that silently does nothing.
    show('miComparePanel', compareApplies());
    show('miCompareFields', S.compareOn && compareApplies());
    show('miCompareHint', !S.compareOn);
    // Three photos or more in a strip is the shape the "photos into a PDF"
    // search is actually about, and that is a different tool. A pair is a
    // comparison and gets no such nudge.
    show('miPdfHint', S.items.length >= 3 && !isOverlay);
    $('miCanvasBox').classList.toggle('grab', isOverlay);
    $('miSelHint').textContent = isOverlay
        ? t('layer_hint')
        : (S.items.length > 1 ? t('select_hint') : t('layer_selected', { n: 1 }));
    // A collapsed drawer is not merely untidy — its controls are unreachable.
    // Switching to Layers would otherwise show a stack of images with no way to
    // set opacity or blend without hunting for it.
    if (isOverlay && !$('miPhotosDrawer').open) $('miPhotosDrawer').open = true;
    syncDrawerSubs();
    syncMaskUI();
}

/* ── «Совместное фото»: дверь в генератор картинок ──────────────────────
   Отзыв 26.10.2026 с этой страницы: «Пусть они стоят вместе в обнимку». Это
   новый снимок с новыми позами — его рисует модель, а склейка и слои кладут
   фото как есть. Строка стоит под «Скачать», пока на странице 2–10 фото (столько
   берёт Nano Banana 2.1), и молчит у скриншотов: им склейка, а не объятия.
   Элемента нет на tembrica.com (генератор закрыт кассой) и на вчерашней
   разметке — поэтому его ищут, а не предполагают. */
let togetherShownFor = '';
function togetherDoorApplies() {
    const n = S.items.length;
    return n >= 2 && n <= 10 && !S.autoStitch && !S._offer;
}
function syncTogetherDoor() {
    const door = $('miTogether');
    if (!door) return;
    const on = togetherDoorApplies();
    show('miTogether', on);
    if (on && togetherShownFor !== setKey()) {
        togetherShownFor = setKey();
        ae('feature_use', { f: 'together_door_shown', n: S.items.length, mode: S.mode });
    }
}
function wireTogetherDoor() {
    const a = $('miTogetherGo');
    if (!a) return;
    a.addEventListener('click', (e) => {
        const th = window.__toolHandoff;
        const url = a.getAttribute('href') || '';
        const files = S.items.slice(0, 10).map((it) => it.d && it.d.file).filter(Boolean);
        // Шаблон едет и запросом: без переноса (старый браузер, нет места) человек
        // всё равно попадёт в «Совместное фото», только фото добавит сам.
        const target = url.replace(/(#|$)/, (url.indexOf('?') === -1 ? '?' : '&') + 'tpl=together$1');
        ae('feature_use', { f: 'together_door', n: S.items.length, mode: S.mode });
        if (!files.length || !th || typeof th._putMany !== 'function' || typeof th.available !== 'function' || !th.available()) {
            e.preventDefault();
            location.href = target;
            return;
        }
        e.preventDefault();
        a.setAttribute('aria-busy', 'true');
        th._putMany(files.map((f) => ({ blob: f, filename: f.name || 'photo.jpg' })), SLUG, { mode: 'edit', tpl: 'together', pose: 'hug' })
            .then(() => { location.href = th._withParam(target); })
            .catch(() => { location.href = target; });
    });
}

/**
 * What each folded drawer currently holds, on its own summary line.
 *
 * A drawer hides its state along with its controls, and "why is there a white
 * background on my transparent PNG" is answered by a closed drawer three rows
 * down. The subtitle is the state, so nothing is hidden — only folded.
 */
function syncDrawerSubs() {
    const it = S.items[S.selected];
    const photo = [];
    if (S.harmonize) photo.push(t('sub_tone'));
    if (it && (it.rotate || it.flipH || it.flipV)) photo.push(t('sub_turned'));
    if (S.captionsOn) photo.push(t('sub_captions'));
    $('miPhotosSub').textContent = photo.join(' · ');

    const frame = [];
    if (S.gap) frame.push(t('f_gap') + ' ' + S.gap);
    if (S.padding) frame.push(t('f_padding') + ' ' + S.padding);
    if (S.bgMode === 'color') frame.push(S.bgColor.toUpperCase());
    if (S.borderW) frame.push(t('f_border') + ' ' + S.borderW);
    $('miFrameSub').textContent = frame.join(' · ');

    const file = [];
    if (S.maxOut) file.push(S.maxOut + ' px');
    if (S.filename && S.filename !== 'merged') file.push(S.filename);
    $('miFileSub').textContent = file.join(' · ');
}

function syncSelectedUI() {
    const it = S.items[S.selected];
    if (!it) return;
    $('miOpacity').value = Math.round(it.opacity * 100);
    $('miOpacityV').textContent = Math.round(it.opacity * 100) + '%';
    $('miBlend').value = it.blend;
    $('miScale').value = Math.round(it.scale * 100);
    $('miScaleV').textContent = Math.round(it.scale * 100) + '%';
    $('miAngle').value = Math.round(it.angle);
    $('miAngleV').textContent = Math.round(it.angle) + '°';
    $('miCaptionText').value = it.caption;
    syncMaskUI();
}

function wire() {
    // Mode
    document.querySelectorAll('.mi-mode').forEach((b) => {
        b.addEventListener('click', () => {
            S.mode = b.dataset.mode;
            applyModeDefaults(S.mode);
            ae('feature_use', { f: 'mode', mode: S.mode });
            syncControlsFromState();
            refresh();
            maybeOffer();
            saveSettings();
            draftTouch(true);
        });
    });

    // Layout
    range('miColumns', 'columns', 'miColumnsV');
    $('miFit').addEventListener('change', () => { S.fit = $('miFit').value; scheduleRender(); });
    $('miTarget').addEventListener('change', () => {
        S.matchTarget = $('miTarget').value;
        show('miFldTargetPx', S.matchTarget === 'custom');
        S.items.forEach((it) => { it.sig = null; });
        scheduleRender();
    });
    $('miTargetPx').addEventListener('input', () => { S.targetPx = +$('miTargetPx').value || 1080; scheduleRender(); });
    seg('miAlign', 'align');

    // The frame
    range('miGap', 'gap', 'miGapV', null, () => { touched.add('gap'); syncDrawerSubs(); });
    range('miPad', 'padding', 'miPadV', null, () => { touched.add('padding'); syncDrawerSubs(); });
    range('miRadius', 'radius', 'miRadiusV', null, () => touched.add('radius'));
    range('miBorderW', 'borderW', 'miBorderWV', null, (v) => { show('miFldBorderC', v > 0); syncDrawerSubs(); });
    $('miBgMode').addEventListener('click', (e) => {
        const b = e.target.closest('button');
        if (b) { setBg(b.dataset.v); scheduleRender(); draftTouch(true); }
    });
    $('miBgColor').addEventListener('input', () => { S.bgColor = $('miBgColor').value; scheduleRender(); });
    $('miBorderC').addEventListener('input', () => { S.borderC = $('miBorderC').value; scheduleRender(); });

    // EyeDropper — pick the background straight off the photo. Chromium only,
    // and the button is only mounted where it exists; the swatch beside it is
    // the path everyone else takes and always works.
    if ('EyeDropper' in window) {
        show('miBgPick', true);
        $('miBgPick').addEventListener('click', async () => {
            try {
                const res = await new window.EyeDropper().open();
                if (res && res.sRGBHex) {
                    S.bgColor = res.sRGBHex;
                    $('miBgColor').value = res.sRGBHex;
                    if (S.bgMode !== 'color') setBg('color');
                    ae('feature_use', { f: 'eyedropper' });
                    scheduleRender();
                    draftTouch(true);
                }
            } catch (_) { /* the user pressed Escape — not an error */ }
        });
    }

    // Photos — tone match
    if (Harmonize.supported()) {
        show('miHarmonizeWrap', true);
        $('miHarmonize').addEventListener('change', () => {
            S.harmonize = $('miHarmonize').checked;
            show('miFldHarmRef', S.harmonize);
            ae('feature_use', { f: 'harmonize', on: S.harmonize ? 1 : 0, n: S.items.length });
            scheduleRender();
            saveSettings();
        });
        $('miHarmRef').addEventListener('change', () => {
            S.harmonizeRef = $('miHarmRef').value;
            ae('feature_use', { f: 'harmonize_ref', ref: S.harmonizeRef });
            scheduleRender();
            saveSettings();
        });
    }

    // Photos — the selected one
    $('miRotate').addEventListener('click', () => {
        const it = S.items[S.selected]; if (!it) return;
        it.rotate = (it.rotate + 90) % 360; it.sig = null; it.tone = null;
        ae('feature_use', { f: 'rotate' });
        refresh();
        draftTouch(true);
    });
    $('miFlipH').addEventListener('click', () => {
        const it = S.items[S.selected]; if (!it) return;
        it.flipH = !it.flipH;
        ae('feature_use', { f: 'flip' });
        refresh();
        draftTouch(true);
    });
    $('miCaptionText').addEventListener('input', () => {
        const it = S.items[S.selected]; if (!it) return;
        it.caption = $('miCaptionText').value;
        // Typing a caption IS switching captions on. A separate "show captions"
        // checkbox meant typing into a field that changed nothing, which reads
        // as the tool being broken.
        syncCaptions();
        scheduleRender();
    });
    range('miCaptionSize', 'captionSize', 'miCaptionSizeV');
    $('miCaptionColor').addEventListener('input', () => { S.captionColor = $('miCaptionColor').value; scheduleRender(); });

    // Overlay layer controls
    const blendSel = $('miBlend');
    Compose.BLEND_MODES.forEach((m) => {
        const o = document.createElement('option');
        o.value = m;
        o.textContent = t('blend_' + m.replace(/-/g, '_')) || m;
        blendSel.appendChild(o);
    });
    blendSel.addEventListener('change', () => {
        const it = S.items[S.selected]; if (!it) return;
        it.blend = blendSel.value;
        ae('feature_use', { f: 'blend', mode: it.blend });
        scheduleRender();
    });
    $('miOpacity').addEventListener('input', () => {
        const it = S.items[S.selected]; if (!it) return;
        it.opacity = +$('miOpacity').value / 100;
        it.opacityByHand = true;
        $('miOpacityV').textContent = $('miOpacity').value + '%';
        scheduleRender();
    });
    $('miScale').addEventListener('input', () => {
        const it = S.items[S.selected]; if (!it) return;
        it.scale = +$('miScale').value / 100;
        $('miScaleV').textContent = $('miScale').value + '%';
        scheduleRender();
    });
    $('miAngle').addEventListener('input', () => {
        const it = S.items[S.selected]; if (!it) return;
        it.angle = +$('miAngle').value;
        $('miAngleV').textContent = $('miAngle').value + '°';
        scheduleRender();
    });
    $('miCanvasMode').addEventListener('change', () => { S.canvasMode = $('miCanvasMode').value; scheduleRender(); });

    // Comparison
    $('miCompareOn').addEventListener('change', () => {
        if ($('miCompareOn').checked) enableCompare(false);
        else {
            S.compareOn = false;
            show('miCompareFields', false);
            ae('feature_use', { f: 'compare_off' });
            scheduleRender();
        }
    });
    $('miLabelA').addEventListener('input', () => { S.labelA = $('miLabelA').value; scheduleRender(); });
    $('miLabelB').addEventListener('input', () => { S.labelB = $('miLabelB').value; scheduleRender(); });
    range('miDivider', 'divider', 'miDividerV');
    $('miDividerC').addEventListener('input', () => { S.dividerC = $('miDividerC').value; scheduleRender(); });

    // Stitch
    $('miAutoStitch').addEventListener('change', () => {
        if ($('miAutoStitch').checked) {
            S.autoStitch = true;
            show('miStitchOffer', false);
            S._offer = null;
            runAutoStitch();
            scheduleRender();
        } else revertStitch('switch');
    });
    // A crop set by hand is the person's: the switch stops measuring over it.
    range('miTrimTop', 'trimTop', 'miTrimTopV', null, stitchByHand);
    range('miTrimBottom', 'trimBottom', 'miTrimBottomV', null, stitchByHand);
    // Looked up, not assumed. A page rendered before this line existed (an
    // edge cache, a service-worker copy shown while the network is slow) asks
    // for THIS script: /jsv/{ver}/ serves the current file under any version.
    // A missing optional button there must not throw and take the wiring below
    // — the format, the name, Download, the uploader — down with it.
    const undoStitch = $('miStitchUndo');
    if (undoStitch) undoStitch.addEventListener('click', () => revertStitch('bar'));

    // Export
    $('miFormat').addEventListener('click', (e) => {
        const b = e.target.closest('button'); if (!b) return;
        Array.from($('miFormat').querySelectorAll('button')).forEach((x) => {
            x.setAttribute('aria-pressed', String(x === b));
            x.classList.toggle('on', x === b);
        });
        S.format = b.dataset.v;
        show('miFldQuality', S.format !== 'png');
        scheduleRender();
        draftTouch(true);
    });
    range('miQuality', 'quality', 'miQualityV', (v) => v + '%');
    $('miMaxOut').addEventListener('change', () => { S.maxOut = +$('miMaxOut').value; syncDrawerSubs(); scheduleRender(); });
    $('miFilename').addEventListener('input', () => { S.filename = safeName($('miFilename').value); syncDrawerSubs(); });

    // Inline fixes
    $('miAlphaFix').addEventListener('click', () => {
        S.format = 'png';
        Array.from($('miFormat').querySelectorAll('button')).forEach((x) => {
            x.setAttribute('aria-pressed', String(x.dataset.v === 'png'));
            x.classList.toggle('on', x.dataset.v === 'png');
        });
        show('miFldQuality', false);
        ae('feature_use', { f: 'alpha_fix' });
        scheduleRender();
        draftTouch(true);
    });
    $('miUpscaleFix').addEventListener('click', () => {
        S.matchTarget = 'min';
        $('miTarget').value = 'min';
        show('miFldTargetPx', false);
        ae('feature_use', { f: 'upscale_fix' });
        scheduleRender();
        draftTouch(true);
    });
    $('miStitchOfferYes').addEventListener('click', acceptStitchOffer);
    $('miStitchOfferNo').addEventListener('click', () => {
        show('miStitchOffer', false);
        S._offer = null;
        // …and it stays answered: until 2026-10-07 the very next line asked the
        // detector again, which put the same offer straight back on screen.
        S._offerDeclined = setKey();
        ae('feature_use', { f: 'stitch_offer_dismissed' });
    });
    $('miBigFix').addEventListener('click', () => {
        // Bring the plan back under the budget in one click: cap the long side
        // at a size every device can actually write.
        S.maxOut = 8000;
        $('miMaxOut').value = '8000';
        ae('feature_use', { f: 'budget_fix' });
        scheduleRender();
        draftTouch(true);
    });
    $('miUndoBtn').addEventListener('click', undoRemove);
    // (looked up, not assumed: a page rendered before this bar existed has no such button)
    const undoEmpty = $('miUndoEmptyBtn');
    if (undoEmpty) undoEmpty.addEventListener('click', undoRemove);

    // Actions
    $('miDownload').addEventListener('click', doDownload);
    $('miCopy').addEventListener('click', doCopy);
    $('miReset').addEventListener('click', startOver);
    // Web Share with FILES — the phone route. A merged before/after normally
    // goes straight into a chat, and "download then find it in Files then
    // attach it" is three steps where the platform offers one. Probed with a
    // real File, because `navigator.share` exists on desktop Chrome while
    // `canShare({files})` there is false.
    try {
        const probe = new File([new Blob(['x'], { type: 'image/png' })], 'p.png', { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [probe] })) {
            show('miShare', true);
            $('miShare').addEventListener('click', doShare);
        }
    } catch (_) {}

    // Persist preferences on every settled change — the panel has no Save
    // button and should not need one.
    document.querySelectorAll('#miWorkspace input, #miWorkspace select').forEach((el) => {
        el.addEventListener('change', () => { saveSettings(); syncDrawerSubs(); });
    });
    document.querySelectorAll('.tc-seg, .mi-modes').forEach((el) => {
        el.addEventListener('click', () => setTimeout(saveSettings, 0));
    });
    // …and every value in the workspace is part of the WORK too. Typed or let go,
    // it is an edit; a slider or a colour still moving only keeps a work that has
    // already begun (tool-draft-editor.js, touch(counted)).
    $('miWorkspace').addEventListener('input', (e) => {
        const ty = e.target && e.target.type;
        draftTouch(ty === 'text' || ty === 'number');
    });
    $('miWorkspace').addEventListener('change', () => draftTouch(true));

    // Files arrive through ONE door. The tool used to also listen for `paste`
    // on the document, which the shared uploader already does — one Ctrl+V
    // added the image twice (measured: 2 for 1, 4 for 2).
    const drop = $('miDrop');
    if (drop) {
        const onFiles = (f, detail) => {
            const d = detail || {};
            addFiles(d.files && d.files.length ? d.files : (d.file ? [d.file] : (f ? [f] : [])), d.source);
        };
        /* Through the uploader's own binder, not a bare listener. The zone is
           wired long before this module boots — measured on production with a
           cold cache, 07.10.2026: 3 s apart on a desktop, about 20 s at a
           phone's CPU — and a pick made in between was dispatched to nobody:
           the zone said «2 files», the page stayed empty. The binder replays
           that pick (tool-uploader.js, bindUploaderFile). */
        // …unless it waited longer than the binder will replay (30 s). Then it
        // is not coming, and the zone must not go on saying it holds those
        // files. (Attached first: the binder says so from inside the call.)
        drop.addEventListener('tool-uploader:reject', (e) => {
            if (e.detail && e.detail.reason === 'pending_expired') syncZone();
        });
        if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(drop, onFiles);
        else drop.addEventListener('tool-uploader:file', (e) => onFiles(null, e.detail));
        drop.addEventListener('tool-uploader:clear', () => { if (S.items.length) startOver(); });
    }

    let resizeRaf = 0;
    window.addEventListener('resize', () => {
        if (resizeRaf) return;
        resizeRaf = requestAnimationFrame(() => { resizeRaf = 0; renderPreview(); });
    });
    document.addEventListener('keydown', (e) => {
        if (S.mode === 'overlay' && !(window.__keyOwnedByFocus && window.__keyOwnedByFocus(e))) {
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.code === 'KeyZ' && maskUndo.length) {
                if (undoMask()) { e.preventDefault(); return; }
            }
            if (brushOn() && !e.ctrlKey && !e.metaKey && (e.key === '[' || e.key === ']')) {
                brush.size = Math.max(4, Math.min(300, Math.round(brush.size * (e.key === ']' ? 1.2 : 1 / 1.2))));
                saveBrush(); syncMaskUI(); scheduleRender();
                e.preventDefault();
                return;
            }
        }
        if (e.target && /input|select|textarea/i.test(e.target.tagName)) return;
        if (e.target && e.target.closest && e.target.closest('.mi-thumb')) return;
        if ((e.key === 'Delete' || e.key === 'Backspace') && S.items.length) {
            e.preventDefault(); removeItem(S.selected);
        }
    });
    wireCanvasPointer();
    wireTogetherDoor();
    wireMask();
}

/**
 * Direct manipulation on the preview, overlay mode.
 *
 * Three gestures on one pointer stream: a corner grip scales about the centre,
 * the arm grip rotates, anything else inside a layer moves it. The grips are
 * tested FIRST and in front-to-back order, because they stick out past the box
 * they belong to and a hit test that starts with the boxes would hand a corner
 * grip to whatever is behind it.
 */
function wireCanvasPointer() {
    if (!canvas) return;
    let drag = null;
    const toPlan = (e) => {
        const r = canvas.getBoundingClientRect();
        const k = lastPlan ? lastPlan.width / r.width : 1;
        return { x: (e.clientX - r.left) * k, y: (e.clientY - r.top) * k };
    };
    const near = (p, q) => {
        const tol = (HANDLE + 5) / Math.max(0.0001, view.scale);
        return Math.abs(p.x - q.x) <= tol && Math.abs(p.y - q.y) <= tol;
    };

    canvas.addEventListener('pointerdown', (e) => {
        if (S.mode !== 'overlay' || !lastPlan) return;
        // A brush is chosen: the press paints the selected layer, it does not grab one.
        if (brushOn()) {
            const pt = toPlan(e);
            brushAt = pt;
            if (startStroke(pt)) {
                drag = { kind: 'paint' };
                try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
                e.preventDefault();
            }
            return;
        }
        const p = toPlan(e);
        const sel = lastPlan.placements.find((q) => q.i === S.selected);

        if (sel) {
            const g = gripsFor(sel, lastPlan);
            if (near(p, g.rotate)) {
                drag = { kind: 'rotate', i: sel.i, c: g.centre, start: S.items[sel.i].angle,
                    a0: Math.atan2(p.y - g.centre.y, p.x - g.centre.x) };
            } else {
                for (const c of g.corners) {
                    if (near(p, c)) {
                        const d0 = Math.hypot(p.x - g.centre.x, p.y - g.centre.y);
                        drag = { kind: 'scale', i: sel.i, c: g.centre, d0: Math.max(1, d0), s0: S.items[sel.i].scale };
                        break;
                    }
                }
            }
        }
        if (!drag) {
            for (let i = lastPlan.placements.length - 1; i >= 0; i--) {
                const q = lastPlan.placements[i];
                if (p.x >= q.x && p.x <= q.x + q.w && p.y >= q.y && p.y <= q.y + q.h) {
                    if (q.i !== S.selected) { S.selected = q.i; renderStrip(); syncSelectedUI(); }
                    drag = { kind: 'move', i: q.i, ox: p.x, oy: p.y, sx: S.items[q.i].x, sy: S.items[q.i].y };
                    break;
                }
            }
        }
        if (!drag) return;
        try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
        e.preventDefault();
        scheduleRender();
    });

    canvas.addEventListener('pointermove', (e) => {
        if (brushOn() && lastPlan) {
            const pt = toPlan(e);
            brushAt = pt;
            if (drag && drag.kind === 'paint' && stroke) {
                const mp = maskPoint(stroke.p, stroke.it, pt.x, pt.y);
                if (mp) paintTo(mp);
            } else scheduleRender();
            return;
        }
        if (!drag || !lastPlan) return;
        const p = toPlan(e);
        const it = S.items[drag.i];
        if (!it) return;
        const inv = lastPlan.scale || 1;

        if (drag.kind === 'move') {
            it.x = Math.round(drag.sx + (p.x - drag.ox) / inv);
            it.y = Math.round(drag.sy + (p.y - drag.oy) / inv);
            const q = lastPlan.placements.find((z) => z.i === drag.i);
            if (q) snap(it, { x: q.x + (it.x - drag.sx) * inv, y: q.y + (it.y - drag.sy) * inv, w: q.w, h: q.h }, lastPlan);
        } else if (drag.kind === 'scale') {
            const d = Math.hypot(p.x - drag.c.x, p.y - drag.c.y);
            it.scale = Math.max(0.05, Math.min(4, drag.s0 * (d / drag.d0)));
            $('miScale').value = Math.round(it.scale * 100);
            $('miScaleV').textContent = Math.round(it.scale * 100) + '%';
        } else if (drag.kind === 'rotate') {
            const a = Math.atan2(p.y - drag.c.y, p.x - drag.c.x);
            let deg = drag.start + (a - drag.a0) * 180 / Math.PI;
            // Shift, or simply passing close to a right angle, settles on it:
            // "almost straight" is the one thing nobody wants.
            const snapped = Math.round(deg / 90) * 90;
            if (e.shiftKey || Math.abs(deg - snapped) < 3) deg = snapped;
            it.angle = Math.max(-180, Math.min(180, Math.round(deg)));
            $('miAngle').value = it.angle;
            $('miAngleV').textContent = it.angle + '°';
        }
        scheduleRender();
        draftTouch(false);
    });

    const stop = (e) => {
        if (!drag) return;
        try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}
        // a stroke is counted into the report (tool_complete.strokes), not one event per stroke
        if (drag.kind === 'paint') { drag = null; finishStroke(); scheduleRender(); return; }
        ae('feature_use', { f: 'canvas_' + drag.kind });
        drag = null;
        snapGuides = [];
        scheduleRender();
        draftTouch(true);
    };
    canvas.addEventListener('pointerup', stop);
    canvas.addEventListener('pointercancel', stop);
    canvas.addEventListener('pointerleave', () => { if (brushAt && !stroke) { brushAt = null; scheduleRender(); } });
}

function syncControlsFromState() {
    $('miColumns').value = S.columns; $('miColumnsV').textContent = S.columns;
    $('miFit').value = S.fit;
    $('miTarget').value = S.matchTarget;
    $('miTargetPx').value = S.targetPx;
    show('miFldTargetPx', S.matchTarget === 'custom');
    $('miGap').value = S.gap; $('miGapV').textContent = S.gap;
    $('miPad').value = S.padding; $('miPadV').textContent = S.padding;
    $('miRadius').value = S.radius; $('miRadiusV').textContent = S.radius;
    $('miBgColor').value = S.bgColor;
    $('miBorderW').value = S.borderW; $('miBorderWV').textContent = S.borderW;
    $('miBorderC').value = S.borderC;
    $('miCaptionSize').value = S.captionSize; $('miCaptionSizeV').textContent = S.captionSize;
    $('miCaptionColor').value = S.captionColor;
    $('miQuality').value = S.quality; $('miQualityV').textContent = S.quality + '%';
    $('miMaxOut').value = String(S.maxOut);
    $('miFilename').value = S.filename;
    $('miCanvasMode').value = S.canvasMode;
    $('miHarmonize').checked = S.harmonize;
    $('miHarmRef').value = S.harmonizeRef;
    show('miFldHarmRef', S.harmonize && Harmonize.supported());
    $('miDivider').value = S.divider; $('miDividerV').textContent = S.divider;
    $('miDividerC').value = S.dividerC;
    $('miCompareOn').checked = S.compareOn;
    show('miFldQuality', S.format !== 'png');
    Array.from($('miFormat').querySelectorAll('button')).forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.v === S.format));
        b.classList.toggle('on', b.dataset.v === S.format);
    });
    Array.from($('miAlign').querySelectorAll('button')).forEach((b) => {
        b.setAttribute('aria-pressed', String(b.dataset.v === S.align));
        b.classList.toggle('on', b.dataset.v === S.align);
    });
    setBg(S.bgMode);
}

/* ── output ───────────────────────────────────────────────────────────── */

function setBusy(on, pct) {
    S.busy = on;
    $('miDownload').disabled = on;
    $('miCopy').disabled = on;
    const prog = $('miProg');
    prog.classList.toggle('mi-hidden', !on);
    prog.firstElementChild.style.width = Math.round((pct || 0) * 100) + '%';
}

async function buildBlob() {
    const plan = buildPlan();
    lastPlan = plan;
    return Exporter.render(plan, drawables(), {
        format: S.format,
        quality: S.quality / 100,
        captionStyle: captionStyle(),
        decor: decorFor(plan),
        items: S.items,
        // every step of the export is a sign of life for the shared stall guard
        // (it hears FFmpeg or `stall-tick`, and this export is neither): without it
        // a long export that was still moving was cut at 180 s
        onProgress: (p) => { setBusy(true, p); try { window.dispatchEvent(new Event('stall-tick')); } catch (_) {} },
    });
}

/**
 * One composition = one unit.
 *
 * Per FILE would refuse this tool's own minimum job: a verified account gets
 * two files a day, and merging three screenshots is one intention, not three.
 * Same reasoning and same `set:` key shape as /photo-collage and /split-screen.
 * Repeating the same set later in the day is free — the key is stable.
 */
function limitKey() {
    const keys = S.items.map((it) => `${it.d.name || 'img'}:${it.w}x${it.h}`).sort();
    return ['set:' + keys.join('|')];
}

async function takeTicket() {
    const limit = window.__toolLimit;
    if (!limit || !limit.begin) return { commit() {}, abort() {} };
    const ticket = await limit.begin(SLUG, limitKey());
    if (!ticket || ticket.blocked) return null;
    return ticket;
}

async function doDownload() {
    if (!S.items.length || S.busy) return;
    const ticket = await takeTicket();
    if (!ticket) return;                       // the wall is shown by the primitive
    setBusy(true, 0);
    const started = Date.now();
    stage('encode', { mode: S.mode, n: S.items.length, format: S.format });
    try {
        // The streaming path can run for a minute on a long strip. The shared
        // stall guard turns "the tab looks frozen" into a soft warning and then
        // a reported abort, instead of a user who waits and then leaves.
        const r = window.__withStallGuard
            ? await window.__withStallGuard(() => buildBlob(), { softMs: 25000, hardMs: 180000, stage: 'encode' })
            : await buildBlob();
        const ext = r.format === 'jpeg' ? 'jpg' : r.format;
        const name = `${safeName(S.filename)}-${r.width}x${r.height}.${ext}`;
        if (window.__downloadFile) window.__downloadFile(r.blob, name);
        else {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(r.blob); a.download = name; a.click();
            setTimeout(() => URL.revokeObjectURL(a.href), 4000);
        }
        ticket.commit && ticket.commit();
        draftExported();
        $('miOutWeight').textContent = fmtBytes(r.blob.size);
        if (r.path === 'streamed' && r.flattened) {
            window.__toast && window.__toast(t('streamed_png'), 'info');
        }
        reportComplete(r, started);
    } catch (err) {
        ticket.abort && ticket.abort();        // a failed export is not a delivered result
        reportError(err);
    } finally {
        setBusy(false, 0);
    }
}

function reportComplete(r, started) {
    try {
        const joins = joinsNow();
        const seamsDone = joins.seams || [];
        window.__aeToolComplete && window.__aeToolComplete({
            slug: SLUG, mode: S.mode, n: S.items.length, format: r.format,
            w: r.width, h: r.height, mp: Math.round((r.width * r.height) / 1e6),
            bytes: r.blob.size, path: r.path, flattened: r.flattened ? 1 : 0,
            // the device refused a canvas the table promised — how often the
            // table over-promises (docs/why/primitives/canvas-ceiling.md)
            refused: r.refused ? 1 : 0,
            ms: Date.now() - started,
            // Which settings people actually reach for. Without this the only
            // thing the funnel can say is "they downloaded something".
            fit: S.fit, target: String(S.matchTarget), align: S.align,
            gap: S.gap, pad: S.padding, radius: S.radius,
            bg: S.bgMode, captions: S.captionsOn ? 1 : 0, border: S.borderW ? 1 : 0,
            preset: S._preset || '',
            harmonize: S.harmonize ? 1 : 0,
            harmonize_changed: lastCorrections ? lastCorrections.filter((c) => c && c.changed).length : 0,
            // hidden parts in Layers: how many layers, through which door
            masked: S.items.filter((it) => it.masked).length,
            faded: S.items.filter((it) => it.masked && it.fade && it.fade.side !== 'none').length,
            strokes: maskStats.strokes,
            cutouts: maskStats.cutouts,
            compare: S.compareOn ? 1 : 0,
            // What the joins did to THIS file — from the same door the plan was
            // built through. The counts used to be read off the state, so a
            // side-by-side download reported a column's overlaps it did not have
            // and nothing reported the crops at all.
            stitched: seamsDone.filter((s) => s && s.overlap > 0).length,
            seams_auto: seamsDone.filter((s) => s && s.auto).length,
            seams_corrected: seamsDone.filter((s) => s && s.corrected).length,
            bars: (joins.trims || []).reduce((n, x) => n + x.t + x.b, 0),
            auto_stitch: S.autoStitch ? 1 : 0,
        });
    } catch (_) {}
}

function reportError(err) {
    const code = (err && err.code) || 'export_failed';
    stage('error', { reason: code });
    try {
        window.__aeToolError && window.__aeToolError({
            slug: SLUG, error_type: code, msg: (err && err.message) || '',
            mode: S.mode, n: S.items.length, format: S.format,
            w: lastPlan ? lastPlan.width : 0, h: lastPlan ? lastPlan.height : 0,
        });
    } catch (_) {}
    const msg = code === 'PLAN_TOO_LARGE' ? t('err_too_big')
        : code === 'NO_COMPRESSION_STREAM' ? t('err_no_stream')
            : t('err_export');
    window.__toast ? window.__toast(msg, 'error') : (window.__handleError && window.__handleError(err, SLUG));
}

async function doShare() {
    if (!S.items.length || S.busy) return;
    const ticket = await takeTicket();
    if (!ticket) return;
    setBusy(true, 0);
    const started = Date.now();
    try {
        const r = await buildBlob();
        const ext = r.format === 'jpeg' ? 'jpg' : r.format;
        const file = new File([r.blob], `${safeName(S.filename)}.${ext}`, { type: r.blob.type });
        if (!navigator.canShare || !navigator.canShare({ files: [file] })) {
            // The probe passed with a 1-byte PNG and this real file is refused:
            // almost always a size ceiling. Falling back to the download keeps
            // the user's result in their hands instead of losing it to a toast.
            ticket.commit && ticket.commit();
            window.__downloadFile && window.__downloadFile(r.blob, file.name);
            draftExported();
            window.__toast && window.__toast(t('share_failed'), 'warning');
            return;
        }
        await navigator.share({ files: [file], title: file.name });
        ticket.commit && ticket.commit();
        draftExported();
        ae('feature_use', { f: 'share', bytes: r.blob.size });
        reportComplete(r, started);
    } catch (err) {
        // AbortError is the share sheet being dismissed — a decision, not a
        // failure, and it must not cost the day's quota.
        ticket.abort && ticket.abort();
        if (!err || err.name !== 'AbortError') reportError(err);
    } finally {
        setBusy(false, 0);
    }
}

async function doCopy() {
    if (!S.items.length || S.busy) return;
    const ticket = await takeTicket();
    if (!ticket) return;
    setBusy(true, 0);
    try {
        const plan = buildPlan();
        const r = await Exporter.render(plan, drawables(), {
            format: 'png', captionStyle: captionStyle(), decor: decorFor(plan), items: S.items,
            onProgress: (p) => setBusy(true, p),
        });
        await Exporter.copyToClipboard(r.blob);
        ticket.commit && ticket.commit();
        draftExported();
        ae('feature_use', { f: 'copy_clipboard', bytes: r.blob.size });
        window.__toast && window.__toast(t('copied'), 'success');
    } catch (err) {
        ticket.abort && ticket.abort();
        window.__toast && window.__toast(
            (err && err.code === 'NO_CLIPBOARD') ? t('copy_unsupported') : t('err_export'), 'warning');
    } finally {
        setBusy(false, 0);
    }
}

const DEFAULTS = {
    mode: 'horizontal', columns: 2, fit: 'match', matchTarget: 'max', targetPx: 1080,
    align: 'center', gap: 0, padding: 0, radius: 0,
    bgMode: 'transparent', bgColor: '#ffffff', borderW: 0, borderC: '#ffffff',
    autoStitch: false, trimTop: 0, trimBottom: 0,
    captionsOn: false, captionSize: 28, captionColor: '#ffffff',
    harmonize: false, harmonizeRef: 'first',
    compareOn: false, labelA: '', labelB: '', divider: 6, dividerC: '#ffffff',
    canvasMode: 'first', format: 'png', quality: 92, maxOut: 0, filename: 'merged', selected: 0,
};

/**
 * "Start over" means start over. Clearing the images but keeping the previous
 * run's background colour, labels and stitch state is how the next merge quietly
 * inherits settings the user never chose for it — a transparent logo landing on
 * the white slab left over from a screenshot job. The file name too: kept, it
 * named the next merge after the last one — in its file and in «Мои работы».
 */
function doReset() {
    S.items.forEach((it) => { release(it.d); LayerMask.dropCache(it); });
    brush.tool = 'move';
    brushAt = null;
    stroke = null;
    maskUndo = [];
    S.items = [];
    dropSeams();
    S._preset = '';
    S._offer = null;
    S._offerDeclined = '';
    S._compareFlashed = false;
    touched.clear();
    lastCorrections = null;
    hideUndo();
    show('miStitchOffer', false);
    Object.assign(S, DEFAULTS);
    saveSettings();          // "start over" also forgets the remembered layout
    $('miAutoStitch').checked = false;
    $('miLabelA').value = ''; $('miLabelB').value = '';
    syncControlsFromState();
    syncModeUI();
    syncStitchUI();          // the crops' sliders and the «stitch on» line go with it
    hideWorkspace();
}

/**
 * «Начать заново» and the zone's ×. Through «my work» wherever this browser can
 * keep works: the one on the page stays in the list instead of vanishing, so
 * nothing needs asking. Where it cannot, the tool's own clean start — without a
 * question either, as it always was (the controller would ask one there).
 */
function startOver() {
    ae('feature_use', { f: 'reset' });
    if (drafts && drafts.store()) {
        // the controller writes the work away first and clears the page after
        // (draftFresh): a pick in between must not be cleared with the old work
        if (!resetting) resetting = new Promise((r) => { resetSettle = r; });
        resetsDue++;
        drafts.reset();
    } else draftFresh();
}

/* ── the work survives a crash, a closed tab and F5 ─────────────────────
 *
 * Audit of 30.09.2026: the layout PREFERENCES above were remembered, the WORK
 * was not — the pictures, their order, what was done to each of them and the
 * composition around them lived in memory only; a crash, a closed tab or F5
 * put the person back at an empty upload zone. The shared «my work» controller
 * (public/js/tool-draft-editor.js over tool-draft.js) keeps it; this part says
 * what the work IS, how it comes back and what a new one looks like.
 *
 * Kept: the BYTES of every picture (the File it was decoded from, so a restore
 * goes through the same decode chain as a pick), each picture's own settings,
 * their order, every value of the composition. Not kept: what is derived from
 * the bytes (bitmaps, stitch signatures, tone measurements) — it is derived
 * again — and the moment's UI (the undo of a removal, the offers, open drawers).
 */
let drafts = null;
let draftRev = 0;                          // every change bumps it: the list's picture is redrawn only then
let listPicture = { rev: -1, url: '' };

function draftTouch(counted) {
    draftRev++;
    if (drafts) drafts.touch(counted);
}
function draftExported() { if (drafts) drafts.exported(); }

// What a picture carries besides its bytes.
const ITEM_PROPS = ['rotate', 'flipH', 'flipV', 'opacity', 'blend', 'caption', 'trim', 'x', 'y', 'scale', 'angle', 'fade'];
// The composition. The remembered preferences are part of it as well: a work
// reopens as it was made, not in the layout of the last visit.
const WORK_KEYS = ['mode', 'columns', 'fit', 'matchTarget', 'targetPx', 'align', 'gap', 'padding',
    'radius', 'bgMode', 'bgColor', 'borderW', 'borderC', 'autoStitch', 'trimTop', 'trimBottom',
    'captionsOn', 'captionSize', 'captionColor', 'canvasMode', 'harmonize', 'harmonizeRef',
    'compareOn', 'labelA', 'labelB', 'divider', 'dividerC', 'format', 'quality', 'maxOut',
    'filename', 'selected'];

/** What a person calls it: the file name they gave it, else its pictures. */
function draftLabel() {
    if (S.filename && S.filename !== DEFAULTS.filename) return S.filename;
    const names = S.items.map((it) => it.d.name || 'image');
    return (names.slice(0, 2).join(' + ') + (names.length > 2 ? ' +' + (names.length - 2) : '')).slice(0, 80);
}

/** The merge, small, for the list — drawn from the plan, so without the layer grips. */
function draftPicture() {
    if (listPicture.rev === draftRev) return listPicture.url;
    let url = '';
    try {
        const plan = buildPlan();
        const k = 160 / Math.max(1, plan.width, plan.height);
        const c = document.createElement('canvas');
        c.width = Math.max(1, Math.round(plan.width * k));
        c.height = Math.max(1, Math.round(plan.height * k));
        const cx = c.getContext('2d');
        cx.imageSmoothingQuality = 'high';
        Compose.draw(cx, plan, drawables(), { scale: k, captionStyle: captionStyle(), decor: decorFor(plan) });
        // a JPEG has no alpha: white behind a transparent merge
        cx.globalCompositeOperation = 'destination-over';
        cx.fillStyle = '#fff';
        cx.fillRect(0, 0, c.width, c.height);
        url = c.toDataURL('image/jpeg', 0.7);
    } catch (_) { /* a row without a picture still opens the work */ }
    listPicture = { rev: draftRev, url };
    return url;
}

function draftPayload() {
    // No pictures is no merge: the last save stands until something is added.
    if (!S.items.length) return null;
    const files = [];
    const items = S.items.map((it) => {
        const p = { name: it.d.name || 'image', fi: -1 };
        ITEM_PROPS.forEach((k) => { p[k] = (k === 'trim' || k === 'fade') ? Object.assign({}, it[k]) : it[k]; });
        // the same object on every save: the store writes the bytes once
        if (it.d.file) { p.fi = files.length; files.push(it.d.file); }
        // the erased parts are bytes too — the same Blob while the mask is unchanged
        const mb = LayerMask.maskBlob(it);
        if (mb) { p.mask = { fi: files.length, w: it.mask.w, h: it.mask.h }; files.push(mb); }
        return p;
    });
    const s = {};
    WORK_KEYS.forEach((k) => { s[k] = S[k]; });
    s.seams = Array.from(S.seams, (x) => (x ? Object.assign({}, x) : null));
    // the overlaps of a row, kept apart from a column's — and the mark that
    // says this work was saved with the two apart (see putBack)
    s.seamsH = Array.from(S.seamsH, (x) => (x ? Object.assign({}, x) : null));
    return {
        state: { items, s, touched: Array.from(touched), preset: S._preset || '' },
        files,
        label: draftLabel(),
        preview: draftPicture(),
        meta: { pictures: S.items.length, mode: S.mode },
    };
}

/* Put a saved work back. → the names of the pictures that did not come back. */
async function applyDraft(d) {
    const st = (d && d.state) || {};
    const files = (d && d.files) || [];
    const gen = ++workGen;
    let settle;
    const mine = restoring = new Promise((r) => { settle = r; });
    const items = [], missing = [];
    try {
        for (const p of Array.isArray(st.items) ? st.items : []) {
            if (!p || typeof p !== 'object') continue;
            const f = p.fi >= 0 ? files[p.fi] : null;
            let dd = null;
            // the chain a pick goes through — HEIC, EXIF turn, CMYK, the size guard
            if (f) {
                try { dd = await decodeFile(f, { emit: (e, x) => ae('feature_use', Object.assign({ f: e }, x)) }); }
                catch (_) { dd = null; }
            }
            // «start over» or another work while this one decoded: the page is not
            // this work's any more, and the controller must not adopt it
            if (gen !== workGen) {
                if (dd) release(dd);
                items.forEach((it) => release(it.d));
                throw Object.assign(new Error('restore superseded'), { code: 'SUPERSEDED' });
            }
            if (!dd) { missing.push(p.name || 'image'); continue; }
            dd.file = f;                       // the object the store gave back: its bytes are not written again
            const it = newItem(dd);
            ITEM_PROPS.forEach((k) => {
                if (k === 'trim') { if (p.trim && typeof p.trim === 'object') it.trim = Object.assign({ t: 0, r: 0, b: 0, l: 0 }, p.trim); }
                else if (k === 'fade') {
                    if (p.fade && typeof p.fade === 'object') {
                        it.fade = {
                            side: LayerMask.FADE_SIDES.includes(p.fade.side) ? p.fade.side : 'none',
                            size: Math.max(0.02, Math.min(0.6, +p.fade.size || 0.25)),
                        };
                    }
                }
                else if (p[k] !== undefined && typeof p[k] === typeof it[k]) it[k] = p[k];
            });
            // a work saved before hidden parts existed has no mask: the photo comes back whole
            if (p.mask && p.mask.fi >= 0 && files[p.mask.fi]) {
                await LayerMask.maskFromBlob(it, files[p.mask.fi], p.mask.w | 0, p.mask.h | 0);
            }
            items.push(it);
        }
        putBack(st, items, missing.length > 0);
        return missing;
    } finally {
        settle();
        if (restoring === mine) restoring = null;
    }
}

function putBack(st, items, partial) {
    // the pictures on the page give way to the saved ones
    S.items.forEach((it) => release(it.d));
    S.items = items;
    draftRev++;                        // another work on the page: the list's picture is not this one's
    hideUndo();
    const s = st.s || {};
    WORK_KEYS.forEach((k) => {
        S[k] = (s[k] !== undefined && s[k] !== null && typeof s[k] === typeof DEFAULTS[k]) ? s[k] : DEFAULTS[k];
    });
    S.selected = Math.max(0, Math.min(S.items.length - 1, S.selected | 0));
    // seams join PAIRS of pictures: with one of them missing they would join the wrong ones
    const savedSeams = (list) => (!partial && Array.isArray(list)
        ? list.map((x) => (x && typeof x === 'object' ? Object.assign({}, x) : null)) : []);
    if (Array.isArray(s.seamsH)) {
        S.seams = savedSeams(s.seams);
        S.seamsH = savedSeams(s.seamsH);
    } else {
        /* A work saved before 2026-10-07 has ONE list, and it was applied along
           whichever strip the page was showing. So it belongs to the mode the
           work was saved in — except that a column's MEASURED overlaps were
           carried into a row as they were (the defect): those are not a row's,
           and a row is only ever overlapped by hand. */
        const one = savedSeams(s.seams);
        const byHand = one.every((x) => !x || !x.auto);
        S.seams = S.mode === 'horizontal' ? [] : one;
        S.seamsH = S.mode === 'horizontal' && byHand ? one : [];
    }
    S._offerDeclined = '';
    // which values the person set by hand — a mode switch must keep leaving them alone
    touched.clear();
    (Array.isArray(st.touched) ? st.touched : []).forEach((k) => touched.add(k));
    S._preset = typeof st.preset === 'string' ? st.preset : '';
    S._offer = null; S._compareFlashed = false; S._budgetReported = false;
    lastCorrections = null;

    syncControlsFromState();
    $('miAutoStitch').checked = !!S.autoStitch;
    $('miLabelA').value = S.labelA; $('miLabelB').value = S.labelB;
    show('miStitchOffer', false);
    if (S.items.length) {
        showWorkspace();
        stage('configure', { n: S.items.length });
    } else hideWorkspace();
    // refresh() without runAutoStitch: the seams as they were saved, hand corrections included
    S.items.forEach((it) => { it.sig = null; });
    // (the zone follows from here too — syncZone: the restore did not come through
    // it, and an open zone over restored pictures would push the result off screen)
    renderStrip();
    syncModeUI();
    syncStitchUI();
    syncSelectedUI();
    scheduleRender();
    maybeOffer();
}

/* A new work: no pictures, the defaults, an empty zone. */
function draftFresh() {
    workGen++;
    draftRev++;
    doReset();
    // a pick made after the click IS the new work, and the zone already shows it
    if (!waitingPicks) { try { if (window.ToolUploader) window.ToolUploader.clear('miDrop'); } catch (_) {} }
    if (resetsDue > 0) resetsDue--;
    if (!resetsDue && resetSettle) { const settle = resetSettle; resetSettle = null; resetting = null; settle(); }
}

function draftsBoot() {
    if (!window.__toolDraft || !window.__toolDraft.editor || !$('miDrafts')) return;
    // «my work» is an addition: whatever fails in it must not take the tool down
    try {
        drafts = window.__toolDraft.editor({
            slug: SLUG, root: 'miDrafts', icon: '🖼️',
            payload: draftPayload, apply: applyDraft, fresh: draftFresh,
            started: () => S.items.length > 0 || adding > 0,
        });
        drafts.boot();
    } catch (err) {
        drafts = null;
        try {
            window.__aeToolError && window.__aeToolError({
                slug: SLUG, error_type: 'drafts_boot', msg: String((err && err.message) || err).slice(0, 200),
            });
        } catch (_) {}
    }
}

/* ── boot ─────────────────────────────────────────────────────────────── */

function boot() {
    if (!$('miApp')) return;
    loadSettings();
    wire();
    syncControlsFromState();
    syncModeUI();
    stage('idle', {});
    // The canvas ceiling is no longer probed here: the probe was 0.5–1 GB canvases
    // in a row, +3–4 GB of GPU memory on page load, and it froze a 4 GB laptop
    // (canvas-limits.js). Exporter.canvasLimits() is read where a plan needs it.
    try { ae('device_cap', { tone: Harmonize.supported() ? 1 : 0 }); } catch (_) {}
    draftsBoot();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

window.MergeImages = { S, buildPlan, renderPreview, addFiles, runAutoStitch, enableCompare, gripsFor, decorFor, joinsNow, measureColumn };
